import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import TechnoDivider from "./TechnoDivider";
import { optimizeScene } from "./sceneOptimizer";
import "./LowpolyHero.css";

export default function LowpolyPageHero({ text, text2 }) {
  const mountRef = useRef(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {


// --- Web Audio API Micro Synthesizer for 3D clicks ---
let audioCtx = null;
function playClickSound(type = "click") {
  try {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtx.state === "suspended") audioCtx.resume();

    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);

    const now = audioCtx.currentTime;
    if (type === "lamp") {
      osc.type = "triangle";
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.exponentialRampToValueAtTime(180, now + 0.08);
      gain.gain.setValueAtTime(0.25, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
      osc.start(now);
      osc.stop(now + 0.08);
    } else if (type === "robot") {
      osc.type = "sine";
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.12);
      gain.gain.setValueAtTime(0.25, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
      osc.start(now);
      osc.stop(now + 0.18);
    } else if (type === "phone") {
      osc.type = "sine";
      osc.frequency.setValueAtTime(784, now);
      osc.frequency.setValueAtTime(1046, now + 0.08);
      gain.gain.setValueAtTime(0.25, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc.start(now);
      osc.stop(now + 0.35);
    } else if (type === "bass") {
      osc.type = "triangle";
      osc.frequency.setValueAtTime(140, now);
      osc.frequency.exponentialRampToValueAtTime(38, now + 0.22);
      gain.gain.setValueAtTime(0.4, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.24);
      osc.start(now);
      osc.stop(now + 0.24);
    } else if (type === "plant") {
      osc.type = "sine";
      osc.frequency.setValueAtTime(520, now);
      osc.frequency.exponentialRampToValueAtTime(320, now + 0.15);
      gain.gain.setValueAtTime(0.18, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.16);
      osc.start(now);
      osc.stop(now + 0.16);
    } else if (type === "art") {
      osc.type = "sine";
      osc.frequency.setValueAtTime(587, now);
      osc.frequency.exponentialRampToValueAtTime(1174, now + 0.22);
      gain.gain.setValueAtTime(0.22, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === "mug") {
      osc.type = "sine";
      osc.frequency.setValueAtTime(1200, now);
      osc.frequency.exponentialRampToValueAtTime(400, now + 0.06);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.07);
      osc.start(now);
      osc.stop(now + 0.07);
    } else {
      osc.type = "sine";
      osc.frequency.setValueAtTime(900, now);
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
      osc.start(now);
      osc.stop(now + 0.05);
    }
  } catch (e) {
    // Audio context might be restricted before interaction
  }
}

// --- Scene, Camera, Renderer ---
const mount = mountRef.current;
  if (!mount) return;
const scene = new THREE.Scene();

const PRESETS = {
  cyberpunk: {
    bg: 0x111827,
    fog: 0x111827,
    hemiSky: 0x4f78b8,
    hemiGround: 0x1e293b,
    keyLight: 0x93bbfc,
    biasLight: 0x00f2fe,
    lampLight: 0xffe29d,
    accentColor: 0x00f2fe,
    wallColor: 0x222f46,
    floorColor: 0x1b2436,
  },
  cozy: {
    bg: 0x1c1724,
    fog: 0x1c1724,
    hemiSky: 0xffd2a6,
    hemiGround: 0x36231e,
    keyLight: 0xffeedb,
    biasLight: 0xff9d00,
    lampLight: 0xffc166,
    accentColor: 0xff9d00,
    wallColor: 0x332831,
    floorColor: 0x543b2c,
  },
  sunset: {
    bg: 0x221326,
    fog: 0x221326,
    hemiSky: 0xff708d,
    hemiGround: 0x381937,
    keyLight: 0xffa07a,
    biasLight: 0xff007f,
    lampLight: 0xffc870,
    accentColor: 0xff007f,
    wallColor: 0x35223c,
    floorColor: 0x47283b,
  },
  matrix: {
    bg: 0x091c10,
    fog: 0x091c10,
    hemiSky: 0x184f29,
    hemiGround: 0x0c2915,
    keyLight: 0x66ff99,
    biasLight: 0x00ff66,
    lampLight: 0xadffd0,
    accentColor: 0x00ff66,
    wallColor: 0x143321,
    floorColor: 0x0f2417,
  },
  synthwave: {
    bg: 0x1a0f28,
    fog: 0x1a0f28,
    hemiSky: 0xa855f7,
    hemiGround: 0x2a1040,
    keyLight: 0xf472b6,
    biasLight: 0x9b51e0,
    lampLight: 0xffb86c,
    accentColor: 0xff0080,
    wallColor: 0x2c1945,
    floorColor: 0x201032,
  },
  nordic: {
    bg: 0x0f172a,
    fog: 0x0f172a,
    hemiSky: 0xbae6fd,
    hemiGround: 0x1e293b,
    keyLight: 0xf1f5f9,
    biasLight: 0x38bdf8,
    lampLight: 0xffedd5,
    accentColor: 0x38bdf8,
    wallColor: 0x1e293b,
    floorColor: 0x0f172a,
  }
};

let currentPreset = PRESETS.nordic;

scene.background = new THREE.Color(currentPreset.bg);
scene.fog = new THREE.FogExp2(currentPreset.fog, 0.022);

const w = mount.clientWidth || window.innerWidth;
const h = mount.clientHeight || 520;
const isMob = w < 768;

// --- DEFINITIVE CAMERA VIEW: PROFILO LATERALE ARCHITETTONICO ---
const camera = new THREE.PerspectiveCamera(isMob ? 42 : 33, w / h, 0.1, 100);
camera.position.set(isMob ? 9.0 : 8.4, isMob ? 3.6 : 3.1, isMob ? 5.2 : 3.8);
const cameraTarget = new THREE.Vector3(isMob ? 0.6 : 0.8, isMob ? 1.6 : 1.8, 0.1);
camera.lookAt(cameraTarget);

const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.15));
renderer.setSize(w, h);
renderer.physicallyCorrectLights = true;
renderer.outputEncoding = THREE.sRGBEncoding;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.2;
mount.appendChild(renderer.domElement);

// --- Material Helpers (Stylized Low-Poly) ---
const mat = (color, roughness = 0.65, metalness = 0.08, opts = {}) => {
  const { rotation, segments, material, castShadow, receiveShadow, ...cleanOpts } = opts;
  return new THREE.MeshStandardMaterial({
    color,
    roughness,
    metalness,
    flatShading: true,
    ...cleanOpts
  });
};

// High-performance shadow defaults: only large hero objects cast/receive shadows
const box = (name, size, pos, color, parent = scene, opts = {}) => {
  const g = new THREE.BoxGeometry(...size);
  const m = opts.material || mat(color, opts.roughness ?? 0.65, opts.metalness ?? 0.08, opts);
  const mesh = new THREE.Mesh(g, m);
  mesh.name = name;
  mesh.position.set(...pos);
  mesh.rotation.set(...(opts.rotation || [0, 0, 0]));
  mesh.castShadow = opts.castShadow ?? false;
  mesh.receiveShadow = opts.receiveShadow ?? false;
  parent.add(mesh);
  return mesh;
};

const cyl = (name, r1, r2, h, pos, color, parent = scene, opts = {}) => {
  const g = new THREE.CylinderGeometry(r1, r2, h, opts.segments || 12);
  const m = opts.material || mat(color, opts.roughness ?? 0.65, opts.metalness ?? 0.08, opts);
  const mesh = new THREE.Mesh(g, m);
  mesh.name = name;
  mesh.position.set(...pos);
  mesh.rotation.set(...(opts.rotation || [0, 0, 0]));
  mesh.castShadow = opts.castShadow ?? false;
  mesh.receiveShadow = opts.receiveShadow ?? false;
  parent.add(mesh);
  return mesh;
};

// --- DIORAMA ROOM ARCHITECTURE ---
const room = new THREE.Group();
scene.add(room);

// Floor base with wooden parquet slats & beveled plinth
const floorGroup = new THREE.Group();
room.add(floorGroup);

// Main platform slab (receives shadows)
const floorSlab = box("floorBase", [15.4, 0.4, 11.4], [0, -0.2, 0], currentPreset.floorColor, floorGroup, {
  roughness: 0.8,
  receiveShadow: true
});
// Stylish wooden floor slats
const plankColors = [0x222c3e, 0x253247, 0x1f2838, 0x28364b];
for (let i = -7; i <= 7; i += 1.1) {
  const plank = box("plank", [1.02, 0.03, 11.2], [i, 0.02, 0], plankColors[Math.abs(Math.round(i)) % plankColors.length], floorGroup, {
    roughness: 0.72,
    metalness: 0.05
  });
}

// Low-poly stylish rug under the desk (receives shadows)
const rug = box("rug", [8.4, 0.02, 5.8], [1.4, 0.04, 0.6], 0x151c2e, floorGroup, {
  roughness: 0.95,
  receiveShadow: true
});
// Rug accent border
box("rugBorderL", [0.15, 0.025, 5.8], [-4.1, 0.015, 0], 0x00f2fe, rug, { roughness: 0.5 });
box("rugBorderR", [0.15, 0.025, 5.8], [4.1, 0.015, 0], 0x00f2fe, rug, { roughness: 0.5 });

// Back Wall with real window cutout opening
const backWallMat = mat(currentPreset.wallColor, 0.85, 0.08, { receiveShadow: true });
const backWall = box("backWall", [9.2, 9.0, 0.3], [-3.1, 4.5, -5.6], 0, room, { material: backWallMat });
box("backWallRight", [0.8, 9.0, 0.3], [7.3, 4.5, -5.6], 0, room, { material: backWallMat });
box("backWallTop", [5.4, 2.4, 0.3], [4.2, 7.8, -5.6], 0, room, { material: backWallMat });
box("backWallBottom", [5.4, 2.2, 0.3], [4.2, 1.1, -5.6], 0, room, { material: backWallMat });

// Left Wall
const leftWall = box("leftWall", [0.3, 9.0, 11.4], [-7.6, 4.3, 0], 0x131924, room, {
  castShadow: false,
  receiveShadow: true,
  roughness: 0.85
});

// Wall Baseboards
box("baseboardBack", [15.4, 0.35, 0.08], [0, 0.18, -5.44], 0x0a0e16, room, { castShadow: false });
box("baseboardLeft", [0.08, 0.35, 11.4], [-7.44, 0.18, 0], 0x0a0e16, room, { castShadow: false });

// Acoustic wood slatted studio wall on left wall (Clean, premium Japandi studio acoustic treatment)
const acousticPanel = new THREE.Group();
acousticPanel.position.set(-7.4, 3.8, 0.5);
room.add(acousticPanel);
// Lamelle in legno rimosse: sulla parete ora ci sono neon, quadretti e mensole (restano i faretti)

// --- ARCHITECTURAL TECHNO TRACK SPOTLIGHTS (Tecno Faretti a binario orientabili) ---
let wallLightIsOn = true;
const spotLenses = [];
const spotCones = [];
const goldBezelMat = mat(0xffb800, 0.25, 0.85);

// Ceiling/Wall architectural black aluminum rail
box("spotRail", [0.06, 0.06, 5.8], [0.12, 2.26, 0], 0x141a24, acousticPanel, { metalness: 0.9, roughness: 0.2 });

// Warm ambient point light cast from the spotlights onto the wall
const wallSpotLight = new THREE.PointLight(0xffeedb, 2.5, 5.0, 1.8);
wallSpotLight.position.set(-6.8, 5.8, 0.5);
room.add(wallSpotLight);

const lensMat = new THREE.MeshStandardMaterial({
  color: 0xffeedb,
  emissive: 0xffeedb,
  emissiveIntensity: 2.2,
  roughness: 0.1
});
spotLenses.push(lensMat);
const coneMat = new THREE.MeshBasicMaterial({
  color: 0xffeedb,
  transparent: true,
  opacity: 0.12,
  side: THREE.DoubleSide,
  depthWrite: false
});
spotCones.push(coneMat);
const farettoPositions = [-2.1, -0.7, 0.7, 2.1];
farettoPositions.forEach((fz, idx) => {
  const fGroup = new THREE.Group();
  fGroup.position.set(0.12, 2.22, fz);
  acousticPanel.add(fGroup);

  // Swivel mounting bracket on track
  box("fMount" + idx, [0.06, 0.06, 0.08], [0, 0, 0], 0x1a2230, fGroup, { metalness: 0.85, roughness: 0.25 });
  // Vertical stem
  cyl("fStem" + idx, 0.015, 0.015, 0.08, [0.04, -0.04, 0], 0x141a24, fGroup, { metalness: 0.9 });

  // Directional spotlight canister (angled down towards wall slats)
  const spotCan = new THREE.Group();
  spotCan.position.set(0.06, -0.09, 0);
  spotCan.rotation.z = -0.55; // Angled downwards towards wall
  spotCan.rotation.y = (idx % 2 === 0 ? 0.12 : -0.12);
  fGroup.add(spotCan);

  // Minimalist cylindrical body
  const canBody = cyl("spotCan" + idx, 0.075, 0.075, 0.22, [0, -0.11, 0], 0x121722, spotCan, {
    segments: 14,
    metalness: 0.85,
    roughness: 0.2
  });
  canBody.name = "interactiveWallLight";

  // Gold luxury bezel accent ring
  const bezel = new THREE.Mesh(new THREE.TorusGeometry(0.072, 0.010, 8, 16), goldBezelMat);
  bezel.position.set(0, -0.215, 0);
  bezel.rotation.x = Math.PI / 2;
  spotCan.add(bezel);

  // Glowing optical lens (materiale condiviso: i faretti si accendono/spengono insieme)
  const lens = cyl("lens" + idx, 0.068, 0.068, 0.015, [0, -0.21, 0], 0xffeedb, spotCan, {
    material: lensMat,
    segments: 14
  });
  lens.name = "interactiveWallLight";

  // Subtle downward light cone (luminous beam)
  const cone = new THREE.Mesh(new THREE.ConeGeometry(0.35, 1.6, 12, 1, true), coneMat);
  cone.position.set(0, -1.02, 0);
  cone.rotation.x = Math.PI;
  spotCan.add(cone);
});

function toggleWallLight() {
  playClickSound("lamp");
  wallLightIsOn = !wallLightIsOn;
  wallSpotLight.intensity = wallLightIsOn ? 2.5 : 0.0;
  spotLenses.forEach((mat) => {
    mat.emissiveIntensity = wallLightIsOn ? 2.2 : 0.05;
    mat.color.setHex(wallLightIsOn ? 0xffeedb : 0x221a14);
  });
  spotCones.forEach((mat) => {
    mat.opacity = wallLightIsOn ? 0.12 : 0.0;
  });
}

// --- PARETE SINISTRA (come in home) (neon <DB/>, quadretti progetti, mensole con oggetti 3D) ---
// Asse locale degli oggetti a parete: X = lungo il muro (verso destra a schermo), Z = verso la stanza.
const WALL_X = -7.43; // faccia interna del muro sinistro
const hexCss = (hex) => "#" + hex.toString(16).padStart(6, "0");
const wallGroup = new THREE.Group();
room.add(wallGroup);
const onWall = (y, z) => {
  const g = new THREE.Group();
  g.position.set(WALL_X, y, z);
  g.rotation.y = Math.PI / 2;
  wallGroup.add(g);
  return g;
};
const wallInteractive = [];
const wallKeep = [];

// 1. INSEGNA NEON <DB/> (grande, al centro della parete, sotto i faretti)
// Qui la telecamera è laterale: il neon va nello spazio libero tra mensole e robot, sotto il menu
const neonGroup = onWall(3.25, 0.85);
neonGroup.scale.setScalar(1.55);
const neonCanvas = document.createElement("canvas");
neonCanvas.width = 1536;
neonCanvas.height = 640;
const neonCtx = neonCanvas.getContext("2d");
const neonTexture = new THREE.CanvasTexture(neonCanvas);
const neonMat = new THREE.MeshBasicMaterial({ map: neonTexture, transparent: true, depthWrite: false, toneMapped: false });
const neonPlane = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 1.0), neonMat);
neonPlane.position.z = 0.07;
neonPlane.name = "interactiveNeon";
neonGroup.add(neonPlane);
wallInteractive.push(neonPlane);
wallKeep.push(neonPlane);
[[-1.0, 0.36], [1.0, 0.36], [-1.0, -0.36], [1.0, -0.36]].forEach(([x, y], i) =>
  cyl("neonPin" + i, 0.018, 0.018, 0.07, [x, y, 0.035], 0xc0c8d4, neonGroup, { rotation: [Math.PI / 2, 0, 0], segments: 8, metalness: 0.9, roughness: 0.25 })
);
let neonIsOn = true;
const drawNeon = () => {
  const ctx = neonCtx;
  const accent = hexCss(currentPreset.accentColor);
  ctx.setTransform(1.5, 0, 0, 1.5, 0, 0);
  ctx.clearRect(0, 0, 1024, 427);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  const tube = (text, x, y, font, color, width) => {
    ctx.font = font;
    if (neonIsOn) {
      for (const blur of [60, 28, 10]) {
        ctx.shadowColor = color;
        ctx.shadowBlur = blur;
        ctx.lineWidth = width;
        ctx.strokeStyle = color;
        ctx.strokeText(text, x, y);
      }
      ctx.shadowBlur = 0;
      ctx.lineWidth = width * 0.35;
      ctx.strokeStyle = "rgba(255,255,255,0.95)";
      ctx.strokeText(text, x, y);
    } else {
      ctx.shadowBlur = 0;
      ctx.lineWidth = width * 0.7;
      ctx.strokeStyle = "rgba(160,170,185,0.55)";
      ctx.strokeText(text, x, y);
    }
  };
  tube("<DB/>", 512, 185, '800 230px "JetBrains Mono", monospace', accent, 12);
  tube("full stack dev", 512, 355, '500 64px "JetBrains Mono", monospace', "#ff3d7f", 6);
  neonTexture.needsUpdate = true;
};

// 2. QUADRETTI DEI PROGETTI: fila sotto il neon + composizione verso l'angolo
// Nelle pagine interne niente quadretti: lo spazio è del neon

// 3. MENSOLE CON OGGETTI 3D (parte sinistra della parete)
const SHELF_W = 2.7;
const shelvesZ = 4.0; // più a sinistra che in home, per lasciare spazio al neon
const makeShelf = (y) => {
  const g = onWall(y, shelvesZ);
  box("wShelfPlank", [SHELF_W, 0.06, 0.36], [0, 0, 0.18], 0x3d291d, g, { roughness: 0.5, metalness: 0.08, castShadow: false });
  [-SHELF_W / 2 + 0.25, SHELF_W / 2 - 0.25].forEach((x, i) => box("wShelfBracket" + i, [0.04, 0.16, 0.26], [x, -0.1, 0.13], 0x141a24, g, { metalness: 0.8, roughness: 0.3 }));
  return g;
};
const shelfTop = 0.03;
// Oggetti appoggiati: helper che usano la quota del piano della mensola
const sitBox = (g, name, size, x, z, color, opts = {}) => box(name, size, [x, shelfTop + size[1] / 2, z], color, g, opts);
const sitCyl = (g, name, r1, r2, h, x, z, color, opts = {}) => cyl(name, r1, r2, h, [x, shelfTop + h / 2, z], color, g, opts);

// Mensola alta: libri, pianta, trofeo
const shelfA = makeShelf(4.55);
[[0x61dafb, 0.3], [0xf7df1e, 0.26], [0x68a063, 0.32], [0xdd0031, 0.28], [0x8892bf, 0.3], [0xf8fafc, 0.24], [0x3178c6, 0.31]].forEach(([c, h], i) =>
  sitBox(shelfA, "wBook" + i, [0.075, h, 0.24], -1.15 + i * 0.085, 0.16, c, { roughness: 0.55 })
);
sitBox(shelfA, "wBookLying", [0.3, 0.06, 0.22], -0.45, 0.16, 0x7928ca, { roughness: 0.55 });
sitBox(shelfA, "wBookLying2", [0.27, 0.05, 0.2], -0.45, 0.16, 0xff9d00, { roughness: 0.55 }).position.y += 0.06;
sitCyl(shelfA, "wPot", 0.1, 0.075, 0.16, 0.15, 0.17, 0xe2d6c3, { roughness: 0.8 });
[[0, 0.3, 0, 0.13], [-0.07, 0.25, 0.04, 0.09], [0.08, 0.26, -0.03, 0.1]].forEach(([dx, dy, dz, r], i) => {
  const leaf = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), mat(0x3f8f4a, 0.7, 0.0));
  leaf.position.set(0.15 + dx, shelfTop + dy, 0.17 + dz);
  shelfA.add(leaf);
});
sitCyl(shelfA, "wTrophyBase", 0.07, 0.08, 0.05, 0.7, 0.16, 0x1a1a1a, { roughness: 0.4 });
cyl("wTrophyStem", 0.015, 0.025, 0.1, [0.7, shelfTop + 0.1, 0.16], 0xffb800, shelfA, { metalness: 0.85, roughness: 0.25 });
cyl("wTrophyCup", 0.08, 0.035, 0.12, [0.7, shelfTop + 0.21, 0.16], 0xffb800, shelfA, { metalness: 0.85, roughness: 0.25 });
sitBox(shelfA, "wFrameSmall", [0.24, 0.3, 0.03], 1.05, 0.08, 0x111111, { rotation: [-0.12, 0, 0] });
sitBox(shelfA, "wFrameSmallPic", [0.18, 0.22, 0.01], 1.05, 0.1, 0x38bdf8, { rotation: [-0.12, 0, 0], emissive: 0x0c4a6e, emissiveIntensity: 0.6 });

// Mensola centrale: mini TV retrò con console, cubo di Rubik, vinili
const shelfB = makeShelf(3.5);
sitBox(shelfB, "wTvBody", [0.46, 0.36, 0.3], -0.85, 0.17, 0xd9cdb5, { roughness: 0.6 });
box("wTvScreen", [0.34, 0.25, 0.01], [-0.87, shelfTop + 0.19, 0.325], 0x22d3ee, shelfB, { emissive: 0x22d3ee, emissiveIntensity: 1.1, roughness: 0.2 });
[[-0.62, 0.23], [-0.62, 0.15]].forEach(([x, y], i) => cyl("wTvKnob" + i, 0.02, 0.02, 0.02, [x, shelfTop + y, 0.325], 0x333333, shelfB, { rotation: [Math.PI / 2, 0, 0], segments: 8 }));
cyl("wTvAntL", 0.006, 0.006, 0.28, [-0.92, shelfTop + 0.48, 0.15], 0x9ca3af, shelfB, { rotation: [0, 0, 0.45], segments: 4, metalness: 0.8 });
cyl("wTvAntR", 0.006, 0.006, 0.28, [-0.78, shelfTop + 0.48, 0.15], 0x9ca3af, shelfB, { rotation: [0, 0, -0.45], segments: 4, metalness: 0.8 });
sitBox(shelfB, "wConsole", [0.34, 0.07, 0.22], -0.3, 0.18, 0x4b5563, { roughness: 0.5 });
sitBox(shelfB, "wConsoleStripe", [0.34, 0.012, 0.222], -0.3, 0.18, 0xdc2626).position.y += 0.03;
sitBox(shelfB, "wPad", [0.16, 0.035, 0.08], -0.3, 0.32, 0x1f2937, { rotation: [0, 0.3, 0] });
const RUBIK = [0xef4444, 0x22c55e, 0x3b82f6, 0xfacc15, 0xf97316, 0xf8fafc];
for (let a = 0; a < 3; a++) for (let b = 0; b < 3; b++) for (let c = 0; c < 3; c++) {
  box("wCube" + a + b + c, [0.042, 0.042, 0.042], [0.25 + (a - 1) * 0.045, shelfTop + 0.025 + b * 0.045, 0.16 + (c - 1) * 0.045], RUBIK[(a * 7 + b * 3 + c * 5) % 6], shelfB, { roughness: 0.4, rotation: [0, 0.35, 0] });
}
[0, 1, 2].forEach((k) => {
  const rec = cyl("wVinyl" + k, 0.17, 0.17, 0.012, [0.75 + k * 0.03, shelfTop + 0.17, 0.12], 0x0f0f0f, shelfB, { rotation: [Math.PI / 2, 0, -0.18 - k * 0.05], segments: 20, roughness: 0.3 });
  cyl("wVinylLabel" + k, 0.055, 0.055, 0.014, [0.75 + k * 0.03, shelfTop + 0.17, 0.12], [0xff7a59, 0xfacc15, 0x38bdf8][k], shelfB, { rotation: [Math.PI / 2, 0, -0.18 - k * 0.05], segments: 12 });
});
sitBox(shelfB, "wSpeakerMini", [0.16, 0.24, 0.16], 1.15, 0.16, 0x1f2937, { roughness: 0.5 });
cyl("wSpeakerCone", 0.05, 0.05, 0.01, [1.15, shelfTop + 0.12, 0.245], 0x111111, shelfB, { rotation: [Math.PI / 2, 0, 0], segments: 12 });

// Mensola bassa: lava lamp, macchina fotografica, mappamondo, clessidra, cactus
const shelfC = makeShelf(2.45);
sitCyl(shelfC, "wLavaBase", 0.07, 0.09, 0.08, -1.0, 0.16, 0x9ca3af, { metalness: 0.8, roughness: 0.3 });
cyl("wLavaGlass", 0.045, 0.075, 0.26, [-1.0, shelfTop + 0.21, 0.16], 0xff3d7f, shelfC, { emissive: 0xff3d7f, emissiveIntensity: 0.9, roughness: 0.1 });
cyl("wLavaCap", 0.04, 0.045, 0.05, [-1.0, shelfTop + 0.365, 0.16], 0x9ca3af, shelfC, { metalness: 0.8, roughness: 0.3 });
sitBox(shelfC, "wCamBody", [0.22, 0.14, 0.11], -0.5, 0.18, 0x1f2937, { roughness: 0.45 });
cyl("wCamLens", 0.05, 0.055, 0.09, [-0.5, shelfTop + 0.07, 0.27], 0x111111, shelfC, { rotation: [Math.PI / 2, 0, 0], segments: 12, metalness: 0.6, roughness: 0.3 });
sitBox(shelfC, "wCamTop", [0.08, 0.03, 0.06], -0.56, 0.18, 0x9ca3af).position.y += 0.14;
sitCyl(shelfC, "wGlobeBase", 0.07, 0.08, 0.03, 0.05, 0.16, 0x3d291d);
cyl("wGlobeStem", 0.01, 0.01, 0.1, [0.05, shelfTop + 0.08, 0.16], 0xffb800, shelfC, { metalness: 0.85, segments: 6 });
const globe = new THREE.Mesh(new THREE.IcosahedronGeometry(0.12, 1), mat(0x2563eb, 0.6, 0.05));
globe.position.set(0.05, shelfTop + 0.24, 0.16);
globe.rotation.z = 0.4;
shelfC.add(globe);
const globeLand = new THREE.Mesh(new THREE.IcosahedronGeometry(0.123, 0), mat(0x22c55e, 0.7, 0.0));
globeLand.scale.set(1, 0.55, 0.7);
globe.add(globeLand);
wallKeep.push(globe);
sitCyl(shelfC, "wHourBase", 0.06, 0.06, 0.02, 0.5, 0.16, 0x3d291d);
cyl("wHourTop", 0.045, 0.008, 0.09, [0.5, shelfTop + 0.15, 0.16], 0xfde68a, shelfC, { roughness: 0.2 });
cyl("wHourBottom", 0.008, 0.045, 0.09, [0.5, shelfTop + 0.065, 0.16], 0xfde68a, shelfC, { roughness: 0.2 });
box("wHourTopCap", [0.12, 0.02, 0.12], [0.5, shelfTop + 0.205, 0.16], 0x3d291d, shelfC);
sitCyl(shelfC, "wCactusPot", 0.07, 0.055, 0.1, 0.95, 0.16, 0xc2410c, { roughness: 0.8 });
cyl("wCactus", 0.04, 0.045, 0.2, [0.95, shelfTop + 0.2, 0.16], 0x4d7c0f, shelfC, { segments: 8, roughness: 0.8 });
cyl("wCactusArm", 0.022, 0.022, 0.09, [0.995, shelfTop + 0.23, 0.16], 0x4d7c0f, shelfC, { segments: 6, roughness: 0.8, rotation: [0, 0, -0.9] });

// 4. CREDENZA BASSA LUNGO IL MURO (riempie la parte bassa) + pianta alta da terra
const CRED_W = 6.2;
const credenza = onWall(0, -1.5);
box("credBody", [CRED_W, 0.9, 0.5], [0, 0.55, 0.25], 0x2b211b, credenza, { roughness: 0.55, castShadow: false, receiveShadow: true });
box("credTop", [CRED_W + 0.06, 0.05, 0.54], [0, 1.025, 0.27], 0x3d291d, credenza, { roughness: 0.45 });
for (let k = 0; k < 4; k++) {
  const x = -CRED_W / 2 + CRED_W / 8 + k * (CRED_W / 4);
  box("credDoor" + k, [CRED_W / 4 - 0.06, 0.78, 0.015], [x, 0.55, 0.505], 0x3a2c22, credenza, { roughness: 0.5 });
  box("credHandle" + k, [0.22, 0.02, 0.02], [x, 0.86, 0.52], 0xc9a227, credenza, { metalness: 0.85, roughness: 0.3 });
}
[-CRED_W / 2 + 0.15, CRED_W / 2 - 0.15].forEach((x, i) => {
  box("credLegF" + i, [0.05, 0.1, 0.05], [x, 0.05, 0.45], 0x141a24, credenza, { metalness: 0.7 });
  box("credLegB" + i, [0.05, 0.1, 0.05], [x, 0.05, 0.05], 0x141a24, credenza, { metalness: 0.7 });
});
const credTop = 1.05;
// Giradischi
box("recBase", [0.5, 0.1, 0.38], [-2.4, credTop + 0.05, 0.27], 0x1f2937, credenza, { roughness: 0.4 });
cyl("recPlatter", 0.16, 0.16, 0.012, [-2.45, credTop + 0.106, 0.27], 0x0f0f0f, credenza, { segments: 20, roughness: 0.3 });
cyl("recLabel", 0.05, 0.05, 0.014, [-2.45, credTop + 0.107, 0.27], 0xff7a59, credenza, { segments: 12 });
box("recArm", [0.02, 0.015, 0.22], [-2.23, credTop + 0.12, 0.25], 0xc0c8d4, credenza, { rotation: [0, 0.35, 0], metalness: 0.85 });
// Vinili appoggiati al muro
[0, 1].forEach((k) => box("recSleeve" + k, [0.34, 0.34, 0.015], [-1.85 + k * 0.05, credTop + 0.17, 0.06 + k * 0.03], [0xfacc15, 0x7928ca][k], credenza, { rotation: [-0.12, 0, 0], roughness: 0.6 }));
// Pila di libri + candela
[[0x61dafb, 0.06], [0xf8fafc, 0.05], [0xdd0031, 0.07]].forEach(([c, h], k) =>
  box("credBook" + k, [0.36 - k * 0.03, h, 0.26], [-1.1, credTop + 0.03 + k * 0.065, 0.27], c, credenza, { roughness: 0.55, rotation: [0, k * 0.12, 0] })
);
cyl("credCandle", 0.045, 0.045, 0.1, [-1.1, credTop + 0.25, 0.27], 0xfef3c7, credenza, { segments: 10, emissive: 0xffb04d, emissiveIntensity: 0.25 });
// Vaso con rami
cyl("credVase", 0.08, 0.11, 0.32, [-0.35, credTop + 0.16, 0.27], 0xe2d6c3, credenza, { segments: 10, roughness: 0.8 });
[[-0.05, 0.35, 0.2], [0.06, 0.4, -0.25], [0, 0.45, 0.05]].forEach(([dx, h, rz], k) =>
  cyl("credBranch" + k, 0.006, 0.01, h, [-0.35 + dx, credTop + 0.32 + h / 2, 0.27], 0x5b3a1e, credenza, { segments: 4, rotation: [0, 0, rz] })
);
// Cornice con foto appoggiata
box("credPhoto", [0.3, 0.38, 0.03], [0.35, credTop + 0.19, 0.12], 0x111111, credenza, { rotation: [-0.12, 0, 0] });
box("credPhotoPic", [0.24, 0.3, 0.01], [0.35, credTop + 0.195, 0.14], 0xff7a59, credenza, { rotation: [-0.12, 0, 0], emissive: 0x7c2d12, emissiveIntensity: 0.35 });
// Cassa bluetooth + succulente
box("credSpeaker", [0.4, 0.2, 0.16], [1.1, credTop + 0.1, 0.27], 0x334155, credenza, { roughness: 0.6 });
[-0.1, 0.1].forEach((dx, k) => cyl("credSpkCone" + k, 0.05, 0.05, 0.01, [1.1 + dx, credTop + 0.1, 0.355], 0x111827, credenza, { rotation: [Math.PI / 2, 0, 0], segments: 10 }));
[1.75, 2.05].forEach((x, k) => {
  cyl("succPot" + k, 0.07, 0.06, 0.09, [x, credTop + 0.045, 0.27], [0xf8fafc, 0xc2410c][k], credenza, { segments: 8 });
  const succ = new THREE.Mesh(new THREE.IcosahedronGeometry(0.07, 0), mat(0x65a30d, 0.7, 0));
  succ.position.set(x, credTop + 0.12, 0.27);
  succ.scale.set(1, 0.7, 1);
  credenza.add(succ);
});
// Lampada da tavolo a fungo (accesa, emissiva: nessuna luce reale in più)
cyl("credLampStem", 0.02, 0.03, 0.3, [2.6, credTop + 0.15, 0.27], 0x9ca3af, credenza, { segments: 8, metalness: 0.7 });
const credLampShade = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), mat(0xfff1d6, 0.4, 0, { emissive: 0xffc98a, emissiveIntensity: 0.9 }));
credLampShade.position.set(2.6, credTop + 0.3, 0.27);
credenza.add(credLampShade);

// Pianta alta da terra a sinistra della credenza, sotto le mensole
const floorPlant = onWall(0, 2.05);
cyl("fpPot", 0.22, 0.17, 0.5, [0, 0.25, 0.32], 0xe2d6c3, floorPlant, { segments: 10, roughness: 0.8 });
cyl("fpTrunk", 0.025, 0.035, 0.9, [0, 0.9, 0.32], 0x5b3a1e, floorPlant, { segments: 5 });
[[0, 1.45, 0, 0.32], [-0.2, 1.2, 0.08, 0.24], [0.2, 1.25, -0.05, 0.26], [0.05, 1.7, 0.05, 0.22], [-0.12, 1.6, -0.1, 0.2]].forEach(([dx, y, dz, r], k) => {
  const f = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), mat([0x3f8f4a, 0x2f7a3c, 0x4fa35a][k % 3], 0.7, 0));
  f.position.set(dx, y, 0.32 + dz);
  floorPlant.add(f);
});

drawNeon();
if (document.fonts && document.fonts.ready) document.fonts.ready.then(drawNeon);

// --- PANORAMIC WINDOW & TOKYO TWILIGHT / SYNTHWAVE SKYLINE (LIGHTWEIGHT & ARTISTIC) ---
const windowGroup = new THREE.Group();
windowGroup.position.set(4.2, 4.4, -5.45);
room.add(windowGroup);

// Window Frame
box("frameTop", [5.8, 0.25, 0.35], [0, 2.4, 0], 0x0d121c, windowGroup);
box("frameBottom", [5.8, 0.35, 0.45], [0, -2.4, 0], 0x0d121c, windowGroup);
box("frameL", [0.25, 4.8, 0.35], [-2.8, 0, 0], 0x0d121c, windowGroup);
box("frameR", [0.25, 4.8, 0.35], [2.8, 0, 0], 0x0d121c, windowGroup);
box("mullion", [0.15, 4.8, 0.3], [0, 0, 0], 0x0d121c, windowGroup);

// Exterior night sky backdrop inside window
const skyGroup = new THREE.Group();
skyGroup.position.set(0, 0, -0.4);
windowGroup.add(skyGroup);

// Painterly Radiant Sunrise / Alba Sky Gradient Canvas ("Ancora piu alba")
const skyCanvas = document.createElement("canvas");
skyCanvas.width = 256;
skyCanvas.height = 512;
const skyCtx = skyCanvas.getContext("2d");
const skyGrad = skyCtx.createLinearGradient(0, 0, 0, 512);
skyGrad.addColorStop(0.0, "#12182b");  // Deep dawn twilight sky
skyGrad.addColorStop(0.30, "#2d234a"); // Morning violet lavender
skyGrad.addColorStop(0.55, "#7b2f5e"); // Vivid dawn magenta-rose
skyGrad.addColorStop(0.74, "#cf4e38"); // Burning dawn coral
skyGrad.addColorStop(0.88, "#f39c50"); // Radiant golden sunrise amber
skyGrad.addColorStop(1.0, "#ffe8c2");  // Glowing dawn horizon light
skyCtx.fillStyle = skyGrad;
skyCtx.fillRect(0, 0, 256, 512);
const skyTexture = new THREE.CanvasTexture(skyCanvas);

const skyBackdropMat = new THREE.MeshBasicMaterial({ map: skyTexture });
const skyBackdrop = new THREE.Mesh(new THREE.PlaneGeometry(6.0, 5.0), skyBackdropMat);
skyBackdrop.position.set(0, 0, 0);
skyGroup.add(skyBackdrop);

// Warm golden horizon dawn haze band
const horizonHazeMat = new THREE.MeshBasicMaterial({
  color: 0xffa055,
  transparent: true,
  opacity: 0.42
});
const horizonHaze = new THREE.Mesh(new THREE.PlaneGeometry(6.0, 1.4), horizonHazeMat);
horizonHaze.position.set(0, -1.8, 0.02);
skyGroup.add(horizonHaze);

// --- REAL PROCEDURAL PALAZZI / CITY BUILDINGS (Real Illuminated Windows & Architecture) ---
const makePalazzoTexture = (cols, rows, litRatio = 0.12, warmRatio = 0.75) => {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 256;
  const ctx = canvas.getContext("2d");

  // Modern dark building facade wall
  ctx.fillStyle = "#111622";
  ctx.fillRect(0, 0, 128, 256);

  const padX = 7;
  const padY = 10;
  const cellW = (128 - padX * 2) / cols;
  const cellH = (256 - padY * 2) / rows;
  const winW = cellW * 0.62;
  const winH = cellH * 0.52;

  // Architectural horizontal floor slabs
  ctx.fillStyle = "#1c2436";
  for (let r = 0; r <= rows; r++) {
    ctx.fillRect(0, padY + r * cellH - 1, 128, 2);
  }

  // Window grid with warm dawn lights and pale reflections (Mostly dark since it's early dawn)
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      const wx = padX + c * cellW + (cellW - winW) / 2;
      const wy = padY + r * cellH + (cellH - winH) / 2;

      // Deterministic pseudo-random seed
      const seed = Math.sin((c + 1) * 31.7 + (r + 1) * 83.1) * 43758.5453;
      const rand = seed - Math.floor(seed);

      if (rand < litRatio) {
        if (rand < litRatio * warmRatio) {
          // Warm dawn interior apartment/office glow
          ctx.fillStyle = rand < litRatio * 0.35 ? "#ffe082" : "#ffd54f";
        } else {
          // Pale morning cyan/sky reflection
          ctx.fillStyle = "#90caf9";
        }
      } else {
        // Dark unlit window with subtle dark reflection tint
        ctx.fillStyle = "#070b12";
      }

      ctx.fillRect(wx, wy, winW, winH);
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.LinearFilter;
  return tex;
};

const cityGroup = new THREE.Group();
skyGroup.add(cityGroup);

const beaconLights = [];

// Materials for building side, roof, and back faces
const bldWallMat = new THREE.MeshBasicMaterial({ color: 0x111622 });
const bldRoofMat = new THREE.MeshBasicMaterial({ color: 0x0c1018 });
const bldBackMat = new THREE.MeshBasicMaterial({ color: 0x080b11 });

const makePalazzo = (name, size, pos, cols, rows, litRatio = 0.12, opts = {}) => {
  const [w, h, d] = size;
  const facadeTex = makePalazzoTexture(cols, rows, litRatio, opts.warmRatio ?? 0.75);
  const facadeMat = new THREE.MeshBasicMaterial({ map: facadeTex });

  // Three.js BoxGeometry face materials: [+X, -X, +Y, -Y, +Z (Front Facade), -Z]
  const mats = [bldWallMat, bldWallMat, bldRoofMat, bldBackMat, facadeMat, bldBackMat];
  const bld = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mats);
  bld.name = name;
  bld.position.set(...pos);
  cityGroup.add(bld);

  // Rooftop elevator/stair penthouse box
  if (opts.penthouse !== false) {
    const pw = w * 0.38;
    const ph = 0.22;
    const pd = d * 0.6;
    const pMesh = new THREE.Mesh(new THREE.BoxGeometry(pw, ph, pd), bldRoofMat);
    pMesh.position.set(opts.penthouseOffset || 0, h / 2 + ph / 2, 0);
    bld.add(pMesh);
  }

  // Rooftop AC chiller units
  if (opts.hvac) {
    const hvac = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.12, 0.2), bldWallMat);
    hvac.position.set(-w * 0.28, h / 2 + 0.06, 0);
    bld.add(hvac);
  }

  // Rooftop antenna with blinking warning beacon (compact height so rooflines are fully visible)
  if (opts.antenna) {
    const aH = opts.antHeight || 0.42;
    const ant = cyl(name + "_ant", 0.012, 0.025, aH, [opts.antX || 0, h / 2 + aH / 2, 0], 0x1f293d, bld, { segments: 6 });
    const bcn = box(name + "_bcn", [0.04, 0.04, 0.04], [0, aH / 2 + 0.02, 0], 0xff0044, ant, {
      emissive: 0xff0044,
      emissiveIntensity: 2.5
    });
    beaconLights.push(bcn);
  }

  return bld;
};

// --- BACKGROUND SILHOUETTE BUILDINGS (Atmospheric depth, lower heights to preserve open dawn sky) ---
const bgBldMat = new THREE.MeshBasicMaterial({ color: 0x161d2d });
const makeBgBld = (w, h, pos) => {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.25), bgBldMat);
  m.position.set(...pos);
  cityGroup.add(m);
};
makeBgBld(0.95, 2.7, [-1.85, -0.95, 0.05]);
makeBgBld(0.85, 2.4, [-0.75, -1.10, 0.05]);
makeBgBld(1.15, 2.9, [0.15, -0.85, 0.05]);
makeBgBld(0.90, 2.5, [1.2, -1.05, 0.05]);
makeBgBld(0.95, 2.2, [2.05, -1.20, 0.05]);

// --- FOREGROUND REAL PALAZZI WITH LOW-POLY WINDOWS (Heights carefully adjusted so rooflines are clearly visible with open sky above) ---
// Palazzo 1 (Far Left): Residential Tower (x: -2.2)
makePalazzo("palazzo1", [0.85, 2.3, 0.45], [-2.2, -1.15, 0.12], 4, 8, 0.10, { hvac: true, antenna: true, antHeight: 0.38 });

// Palazzo 2 (Mid-Left): Commercial Skyscraper with Stepped Rooftop (x: -1.35)
makePalazzo("palazzo2", [1.05, 2.7, 0.55], [-1.35, -0.95, 0.16], 5, 10, 0.12, { penthouseOffset: 0.12, antenna: true, antX: -0.25, antHeight: 0.42 });

// Palazzo 3 (Center-Left): 10-Story Office Palazzo (x: -0.4)
makePalazzo("palazzo3", [0.92, 2.0, 0.45], [-0.4, -1.30, 0.14], 4, 7, 0.09, { hvac: true });

// Palazzo 4 (Center-Right): Landmark Highrise (x: 0.65)
makePalazzo("palazzo4", [1.18, 2.85, 0.6], [0.65, -0.88, 0.18], 5, 11, 0.14, { antenna: true, antX: 0, antHeight: 0.45 });

// Palazzo 5 (Mid-Right): Modern Apartment Building (x: 1.65)
makePalazzo("palazzo5", [0.98, 2.4, 0.5], [1.65, -1.10, 0.15], 5, 9, 0.11, { hvac: true, antenna: true, antX: 0.2, antHeight: 0.38 });

// Palazzo 6 (Far Right): Modern Office Tower (x: 2.38)
makePalazzo("palazzo6", [0.78, 2.1, 0.45], [2.38, -1.25, 0.12], 4, 7, 0.10, { hvac: true });

// Soft drifting dawn clouds catching golden sunrise light
const windowClouds = [];
const makeCloud = (x, y, z, scale, speed) => {
  const cGroup = new THREE.Group();
  cGroup.position.set(x, y, z);
  cGroup.scale.setScalar(scale);
  const cloudMat = new THREE.MeshBasicMaterial({
    color: 0xffeedd,
    transparent: true,
    opacity: 0.82
  });
  cGroup.userData = { speed, cloudMat };
  skyGroup.add(cGroup);

  const gPuff = new THREE.DodecahedronGeometry(0.25, 0);

  const p1 = new THREE.Mesh(gPuff, cloudMat);
  p1.position.set(-0.25, 0, 0);
  cGroup.add(p1);

  const p2 = new THREE.Mesh(gPuff, cloudMat);
  p2.scale.set(1.35, 1.15, 1.15);
  p2.position.set(0.05, 0.07, 0.02);
  cGroup.add(p2);

  const p3 = new THREE.Mesh(gPuff, cloudMat);
  p3.position.set(0.35, -0.04, -0.02);
  cGroup.add(p3);

  const p4 = new THREE.Mesh(gPuff, cloudMat);
  p4.scale.set(0.85, 0.85, 0.85);
  p4.position.set(0.15, 0.22, 0.01);
  cGroup.add(p4);

  windowClouds.push(cGroup);
};

makeCloud(-1.2, 1.45, 0.05, 0.85, 0.0006);
makeCloud(0.3, 1.15, 0.08, 0.95, 0.0008);
makeCloud(1.6, 1.65, 0.05, 0.75, 0.0005);

// --- MODERN DEVELOPER DESK SETUP ---
const desk = new THREE.Group();
desk.position.set(1.4, 0, 0.2);
scene.add(desk);

// Table top with beveled edge and rich dark walnut wood
const deskTop = box("deskTop", [7.4, 0.22, 3.2], [0, 1.6, 0], 0x3d291d, desk, {
  roughness: 0.48,
  metalness: 0.08,
  castShadow: true,
  receiveShadow: true
});
// Wood edge bevel trim
box("deskBevelFront", [7.42, 0.06, 0.04], [0, 1.68, 1.6], 0x5a3e2e, desk, { roughness: 0.4 });

// Sleek black metal trestle legs
const legMat = mat(0x161a22, 0.35, 0.8);
const makeLeg = (x, z) => {
  const legG = new THREE.Group();
  legG.position.set(x, 0, z);
  desk.add(legG);
  box("legPillar1", [0.18, 1.5, 0.18], [0, 0.75, -0.6], 0x161a22, legG, { material: legMat });
  box("legPillar2", [0.18, 1.5, 0.18], [0, 0.75, 0.6], 0x161a22, legG, { material: legMat });
  box("legCross", [0.14, 0.14, 1.4], [0, 0.75, 0], 0x161a22, legG, { material: legMat });
  box("legFoot", [0.26, 0.1, 1.6], [0, 0.05, 0], 0x161a22, legG, { material: legMat });
  box("legTopPlate", [0.26, 0.08, 1.5], [0, 1.48, 0], 0x161a22, legG, { material: legMat });
};
makeLeg(-3.0, 0);
makeLeg(3.0, 0);

// Oversized felt desk pad / mat (clean, premium dark felt)
const deskPad = box("deskPad", [5.2, 0.025, 2.2], [0, 1.72, 0.2], 0x131822, desk, {
  roughness: 0.95,
  receiveShadow: true
});

// --- ANIMATED TERMINAL CANVAS TEXTURE FOR MONITOR ---
const termCanvas = document.createElement("canvas");
termCanvas.width = 1024;
termCanvas.height = 512;
const ctx = termCanvas.getContext("2d");
const termTexture = new THREE.CanvasTexture(termCanvas);
termTexture.anisotropy = 4;

let codeLines = [
  "// Welcome to Davide Balice 3D Creative Studio",
  "import { CreativeAI, WebGL3D, Innovation } from '@balice/core';",
  "",
  "const developer = new CreativeCoder({",
  "  name: 'Davide Balice',",
  "  passion: '3D Web Experiences & Full-Stack Apps',",
  "  status: 'Crafting the Future with Three.js & AI'",
  "});",
  "",
  "await developer.renderMagic({ quality: 'PREMIUM_LOWPOLY' });",
  "console.log('✨ System initialized: 60 FPS Smooth Render');"
];

let termCursorTick = 0;
let codeTypedIndex = 0;
let currentCodeSnippet = 0;

const SNIPPETS = [
  [
    "// 🚀 Davide Balice — Full-Stack & 3D Web",
    "import { ThreeEngine, ShaderPass } from 'three/addons';",
    "",
    "export async function launchWorld() {",
    "  const scene = new LowPolyDiorama();",
    "  scene.addLighting({ mood: 'Cinematic Dual-Tone' });",
    "  scene.optimizeGeometry({ lowPoly: true, flatShading: true });",
    "  return scene.ignite();",
    "}",
    "// Click anywhere to interact with 3D elements!"
  ],
  [
    "// ⚡ Matrix System Protocol",
    "while (true) {",
    "  const stream = matrix.readIncomingStreams();",
    "  renderQuantumNeurons(stream);",
    "  aiAssistant.optimizeWorkflows();",
    "  dispatch(CREATE_HIGH_IMPACT_SOLUTIONS);",
    "}",
    "// Status: 100% Online & Scalable"
  ],
  [
    "// 🎨 Creative UI/UX Component",
    "const HeroSection = () => (",
    "  <Stage environment='studio' shadows='soft'>",
    "    <LowPolyScene interactive={true} />",
    "    <FloatingParticles count={70} glow={true} />",
    "    <GlassmorphismTopbar branding='DB>' />",
    "  </Stage>",
    ");"
  ]
];

function drawTerminal() {
  ctx.fillStyle = "#0c121e";
  ctx.fillRect(0, 0, termCanvas.width, termCanvas.height);

  // Terminal window topbar
  ctx.fillStyle = "#151e30";
  ctx.fillRect(0, 0, termCanvas.width, 42);

  // Traffic lights
  ctx.fillStyle = "#ff5f56";
  ctx.beginPath();
  ctx.arc(30, 21, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ffbd2e";
  ctx.beginPath();
  ctx.arc(56, 21, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#27c93f";
  ctx.beginPath();
  ctx.arc(82, 21, 8, 0, Math.PI * 2);
  ctx.fill();

  // Tab Title
  ctx.font = "bold 18px 'JetBrains Mono', monospace";
  ctx.fillStyle = "#94a3b8";
  ctx.fillText("davide-balice-dev.ts — zsh", 120, 27);

  // Code line drawing
  ctx.font = "20px 'JetBrains Mono', monospace";
  const linesToDraw = SNIPPETS[currentCodeSnippet];
  let y = 82;

  linesToDraw.forEach((line, idx) => {
    // Syntax coloring
    if (line.startsWith("//")) {
      ctx.fillStyle = "#64748b";
    } else if (line.includes("import") || line.includes("export") || line.includes("const") || line.includes("await") || line.includes("function") || line.includes("while")) {
      ctx.fillStyle = "#ff007f";
    } else if (line.includes("'") || line.includes('"')) {
      ctx.fillStyle = "#00f2fe";
    } else if (line.includes("console") || line.includes("scene") || line.includes("developer")) {
      ctx.fillStyle = "#ffbd2e";
    } else {
      ctx.fillStyle = "#e2e8f0";
    }

    ctx.fillText(line, 40, y);
    y += 34;
  });

  // Blinking cursor
  termCursorTick++;
  if (Math.floor(termCursorTick / 25) % 2 === 0) {
    ctx.fillStyle = "#00f2fe";
    ctx.fillRect(40 + (linesToDraw[linesToDraw.length - 1]?.length || 0) * 12.2, y - 34, 12, 22);
  }

  termTexture.needsUpdate = true;
}
drawTerminal();

// --- ULTRAWIDE CURVED MONITOR ---
const monitor = new THREE.Group();
monitor.position.set(0, 1.72, -0.4);
monitor.scale.setScalar(0.92); // Refined: slightly more compact with elegant margins
desk.add(monitor);

// Heavy metal stand
box("monitorBase", [1.6, 0.08, 0.9], [0, 0.04, 0.15], 0x121720, monitor, { roughness: 0.3, metalness: 0.85 });
box("monitorPole", [0.24, 1.4, 0.18], [0, 0.7, -0.15], 0x161d28, monitor, { roughness: 0.3, metalness: 0.85 });
box("monitorPivot", [0.4, 0.35, 0.22], [0, 1.25, -0.08], 0x222a38, monitor, { roughness: 0.4 });

// Monitor screen casing
const screenWidth = 5.2;
const screenHeight = 2.4;
const screenGroup = new THREE.Group();
screenGroup.position.set(0, 1.45, 0.02);
monitor.add(screenGroup);

// Slim bezel frame
box("screenFrame", [screenWidth, screenHeight, 0.12], [0, 0, 0], 0x0f141d, screenGroup, { roughness: 0.3, metalness: 0.4 });

// The dynamic canvas terminal screen surface
const screenMat = new THREE.MeshBasicMaterial({
  map: termTexture
});
const screenSurface = new THREE.Mesh(new THREE.PlaneGeometry(screenWidth - 0.12, screenHeight - 0.12), screenMat);
screenSurface.position.set(0, 0, 0.065);
screenSurface.name = "interactiveScreen";
screenGroup.add(screenSurface);

// Monitor Bias Backlight (Glows behind the monitor onto the back wall)
const monitorBacklight = new THREE.PointLight(currentPreset.biasLight, 4.2, 5.0, 1.8);
monitorBacklight.position.set(0, 1.45, -0.28);
monitor.add(monitorBacklight);

// --- DEV SCREENBAR LIGHTBAR (Mounted on monitor, Clickable!) ---
const screenbar = new THREE.Group();
screenbar.position.set(0, 1.25, 0.08);
screenGroup.add(screenbar);

const screenbarHousing = cyl("screenbarHousing", 0.045, 0.045, 3.6, [0, 0, 0], 0x1a2230, screenbar, {
  rotation: [0, 0, Math.PI / 2],
  segments: 14,
  roughness: 0.35,
  metalness: 0.8
});
screenbarHousing.name = "interactiveScreenbar";

// Screenbar mount bracket
box("barBracket", [0.18, 0.12, 0.22], [0, 0, -0.08], 0x121722, screenbar, { roughness: 0.5 });

// Screenbar downward LED light
const screenbarSpot = new THREE.SpotLight(0xfff0dd, 7.5, 4.0, Math.PI / 3.0, 0.45, 1.2);
screenbarSpot.position.set(0, 0, 0.04);
const screenbarTarget = new THREE.Object3D();
screenbarTarget.position.set(0, -2.4, 0.7);
screenbar.add(screenbarTarget);
screenbarSpot.target = screenbarTarget;
screenbar.add(screenbarSpot);

let screenbarMode = 0; // 0: warm, 1: cool white, 2: cyber rgb, 3: off
const SCREENBAR_MODES = [
  { color: 0xffeedb, intensity: 7.5, name: "Calda 3000K" },
  { color: 0xdff4ff, intensity: 8.5, name: "Fredda 6000K" },
  { color: 0x00f2fe, intensity: 9.5, name: "Cyber RGB" },
  { color: 0x000000, intensity: 0, name: "Spenta" }
];
function cycleScreenbar() {
  screenbarMode = (screenbarMode + 1) % SCREENBAR_MODES.length;
  const m = SCREENBAR_MODES[screenbarMode];
  screenbarSpot.color.setHex(m.color);
  screenbarSpot.intensity = m.intensity;
}

// --- UNDER-DESK CYBER RGB LED STRIP ---
const underdeskRgb = new THREE.PointLight(0x00f2fe, 3.0, 3.5, 1.8);
underdeskRgb.position.set(0, 1.45, 0.4);
desk.add(underdeskRgb);
const underdeskStrip = box("underdeskStrip", [6.8, 0.03, 0.04], [0, 1.48, 0.5], 0x00f2fe, desk, {
  emissive: 0x00f2fe,
  emissiveIntensity: 1.2,
  castShadow: false
});
underdeskStrip.name = "interactiveUnderdesk";

let underdeskColorIndex = 0;
const RGB_COLORS = [0x00f2fe, 0xff007f, 0x00ff88, 0xff9d00, 0x7928ca];
function cycleUnderdeskRgb() {
  underdeskColorIndex = (underdeskColorIndex + 1) % RGB_COLORS.length;
  const col = RGB_COLORS[underdeskColorIndex];
  underdeskRgb.color.setHex(col);
  underdeskStrip.material.color.setHex(col);
  underdeskStrip.material.emissive.setHex(col);
}

// --- PIXAR-STYLE ANGLEPOISE DESK LAMP (Compact & Non-crossing, strictly on right side) ---
const lampGroup = new THREE.Group();
lampGroup.position.set(3.05, 1.72, 0.54);
lampGroup.rotation.y = 3.02; // Rotated inward towards the center of the desk and workspace
desk.add(lampGroup);

const lampMat = mat(0x20293a, 0.35, 0.6);
const lampAccentMat = mat(0x00f2fe, 0.3, 0.7);

// Heavy round base
cyl("lampBase", 0.42, 0.46, 0.09, [0, 0.045, 0], 0x161c28, lampGroup, { material: lampMat, segments: 14 });
// Clickable switch on the base
const lampSwitchMesh = cyl("lampSwitchMesh", 0.08, 0.08, 0.06, [0.18, 0.1, 0.12], 0xff3366, lampGroup, { segments: 10 });
lampSwitchMesh.name = "interactiveLamp";

// Compact lower arm
const lowerArm = new THREE.Group();
lowerArm.position.set(0, 0.09, 0);
lowerArm.rotation.z = 0.18;
lampGroup.add(lowerArm);
box("lowerBar1", [0.05, 0.82, 0.05], [-0.05, 0.41, 0], 0x242e42, lowerArm, { material: lampMat });
box("lowerBar2", [0.05, 0.82, 0.05], [0.05, 0.41, 0], 0x242e42, lowerArm, { material: lampMat });
cyl("joint1", 0.09, 0.09, 0.18, [0, 0.82, 0], 0x00f2fe, lowerArm, { rotation: [0, 0, Math.PI / 2], segments: 10 });

// Compact upper arm
const upperArm = new THREE.Group();
upperArm.position.set(0, 0.82, 0);
upperArm.rotation.z = -0.42;
lowerArm.add(upperArm);
box("upperBar1", [0.05, 0.72, 0.05], [-0.04, 0.36, 0], 0x242e42, upperArm, { material: lampMat });
box("upperBar2", [0.05, 0.72, 0.05], [0.04, 0.36, 0], 0x242e42, upperArm, { material: lampMat });

// Lamp Head Shade (Cone pointing naturally DOWNWARDS and forward towards the desk)
const lampHead = new THREE.Group();
lampHead.position.set(0, 0.72, 0);
// Point cone mouth naturally DOWNWARDS and forward towards the keyboard
lampHead.rotation.set(-0.16, 0.08, 0.68);
upperArm.add(lampHead);

// Wrist swivel joint at connection point
cyl("lampWristJoint", 0.065, 0.065, 0.14, [0, 0, 0], 0x00f2fe, lampHead, {
  rotation: [Math.PI / 2, 0, 0],
  segments: 10
});

// Shade socket / rear cap
cyl("shadeCap", 0.14, 0.14, 0.08, [0, 0.02, 0], 0x161c28, lampHead, { material: lampMat, segments: 12 });

// Flared cone shade: narrow socket at top (0.13), wide flared opening at bottom (0.38)
const lampShade = cyl("lampShade", 0.13, 0.38, 0.44, [0, -0.20, 0], 0x1a2334, lampHead, {
  material: lampMat,
  segments: 14
});
lampShade.name = "interactiveLamp";

// Glowing cyan rim accent around the opening of the shade
cyl("shadeRim", 0.385, 0.385, 0.03, [0, -0.41, 0], 0x00f2fe, lampHead, {
  material: lampAccentMat,
  segments: 14
});

// Glowing light bulb inside the shade (recessed inside the cone)
const bulbMat = new THREE.MeshStandardMaterial({
  color: 0xffeedb,
  emissive: 0xffeedb,
  emissiveIntensity: 1.6,
  roughness: 0.1
});
const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), bulbMat);
bulb.position.set(0, -0.22, 0);
bulb.name = "interactiveLamp";
lampHead.add(bulb);

// Real SpotLight pointing down through the shade mouth towards the desk
const lampSpotlight = new THREE.SpotLight(0xffdb8b, 12, 5.5, Math.PI / 3.2, 0.45, 1.5);
lampSpotlight.position.set(0, -0.15, 0);
lampSpotlight.castShadow = true;
lampSpotlight.shadow.mapSize.set(512, 512);
lampSpotlight.shadow.bias = -0.0005;

// Spotlight target placed along the cone's axis pointing forward/down
const lampTarget = new THREE.Object3D();
lampTarget.position.set(0, -2.8, 0);
lampHead.add(lampTarget);
lampSpotlight.target = lampTarget;
lampHead.add(lampSpotlight);

let lampIsOn = true;
function setLampState(on) {
  lampIsOn = on;
  lampSpotlight.intensity = on ? 12 : 0;
  bulbMat.emissiveIntensity = on ? 1.6 : 0.05;
  const lampBtn = document.querySelector("#lampSwitch");
  if (lampBtn) {
    lampBtn.textContent = on ? "ACCESA" : "SPENTA";
    lampBtn.classList.toggle("active", on);
  }
}

// --- COMPANION ROBOT (Modeled after reference photo with 2 Segway wheels, gold triangle & happy arch eyes) ---
const robot = new THREE.Group();
robot.scale.set(0.76, 0.76, 0.76);
robot.position.set(-2.45, 1.74, 0.75);
robot.rotation.y = 0.38; // Angled gracefully towards the center-right/camera
desk.add(robot);

const rWhiteMat = mat(0xf3f6fa, 0.24, 0.04);
const rBlackMat = mat(0x12151b, 0.4, 0.5);
const rGoldMat = new THREE.MeshStandardMaterial({
  color: 0xffa400,
  roughness: 0.22,
  metalness: 0.65,
  emissive: 0xff7700,
  emissiveIntensity: 0.55
});

// --- SEGWAY BASE & 2 WHEELS ---
const rBase = new THREE.Group();
robot.add(rBase);

// Rounded white chassis
const rChassis = new THREE.Mesh(
  new THREE.SphereGeometry(0.36, 18, 14),
  rWhiteMat
);
rChassis.scale.set(0.95, 0.72, 0.95);
rChassis.position.set(0, 0.24, 0);
rChassis.castShadow = true;
rChassis.name = "interactiveRobot";
rBase.add(rChassis);

// Golden front bumper chin (As seen in photo!)
const frontBumper = box("frontBumper", [0.26, 0.07, 0.12], [0, 0.08, 0.28], 0, rBase, {
  material: rGoldMat
});

// Black undercarriage trim
cyl("underTrim", 0.32, 0.32, 0.05, [0, 0.05, 0], 0, rBase, { material: rBlackMat });

// Two Large Side Wheels (Tires with glowing golden-orange rims)
const wheels = [];
const makeWheel = (side) => {
  const wheelG = new THREE.Group();
  wheelG.position.set(side * 0.42, 0.24, 0);
  rBase.add(wheelG);

  // Black rubber tire
  const tire = new THREE.Mesh(
    new THREE.TorusGeometry(0.18, 0.085, 14, 22),
    mat(0x101318, 0.85, 0.05)
  );
  tire.rotation.y = Math.PI / 2;
  tire.castShadow = true;
  wheelG.add(tire);

  // Tire inner core
  cyl("hub", 0.18, 0.18, 0.14, [0, 0, 0], 0, wheelG, {
    material: rBlackMat,
    rotation: [0, 0, Math.PI / 2],
    segments: 16
  });

  // Glowing golden/orange inner rim (As seen in photo!)
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(0.13, 0.03, 10, 20),
    rGoldMat
  );
  rim.position.set(side * 0.08, 0, 0);
  rim.rotation.y = Math.PI / 2;
  wheelG.add(rim);

  wheels.push(wheelG);
  return wheelG;
};
const wheelL = makeWheel(-1);
const wheelR = makeWheel(1);

// Black articulated waist joint
cyl("waistJoint", 0.16, 0.16, 0.08, [0, 0.44, 0], 0, robot, { material: rBlackMat });

// --- TORSO ---
const rTorso = new THREE.Group();
rTorso.position.set(0, 0.5, 0);
robot.add(rTorso);

// White chest body
const bodyMesh = new THREE.Mesh(
  new THREE.SphereGeometry(0.33, 20, 16),
  rWhiteMat
);
bodyMesh.scale.set(1.02, 1.15, 0.92);
bodyMesh.position.set(0, 0.28, 0);
bodyMesh.castShadow = true;
bodyMesh.name = "interactiveRobot";
rTorso.add(bodyMesh);

// Black side rib accents (as in photo)
box("ribL", [0.08, 0.2, 0.22], [-0.28, 0.22, 0.02], 0, rTorso, { material: rBlackMat, rotation: [0, 0, 0.3] });
box("ribR", [0.08, 0.2, 0.22], [0.28, 0.22, 0.02], 0, rTorso, { material: rBlackMat, rotation: [0, 0, -0.3] });

// Inverted Golden 3D Triangle Emblem (pointing down ▼ as seen in photo!)
const chestTriangle = new THREE.Mesh(
  new THREE.ConeGeometry(0.095, 0.05, 3),
  rGoldMat
);
chestTriangle.rotation.set(-Math.PI / 2, Math.PI, 0);
chestTriangle.position.set(0, 0.32, 0.28);
rTorso.add(chestTriangle);

// Articulated Arms (Segmented black & white)
const makeArm = (side) => {
  const armG = new THREE.Group();
  armG.position.set(side * 0.38, 0.36, 0);
  rTorso.add(armG);

  // White shoulder cap
  const shoulder = new THREE.Mesh(new THREE.SphereGeometry(0.085, 12, 10), rWhiteMat);
  armG.add(shoulder);

  // Black bicep joint
  cyl("bicepJoint", 0.045, 0.045, 0.16, [side * 0.02, -0.1, 0.02], 0, armG, { material: rBlackMat, segments: 10 });
  // White bicep sleeve
  box("bicepSleeve", [0.11, 0.1, 0.11], [side * 0.02, -0.1, 0.02], 0, armG, { material: rWhiteMat });

  // Black elbow joint
  const elbow = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 8), rBlackMat);
  elbow.position.set(side * 0.02, -0.2, 0.02);
  armG.add(elbow);

  // Forearm group
  const forearmG = new THREE.Group();
  forearmG.position.set(side * 0.02, -0.2, 0.02);
  armG.add(forearmG);

  // White forearm sleeve
  cyl("forearmSleeve", 0.055, 0.05, 0.15, [0, -0.09, 0.04], 0, forearmG, { material: rWhiteMat, segments: 10 });

  // Black wrist & hand with fingers (as in photo!)
  const hand = new THREE.Group();
  hand.position.set(0, -0.19, 0.06);
  forearmG.add(hand);

  box("wrist", [0.07, 0.04, 0.08], [0, 0, 0], 0, hand, { material: rBlackMat });
  // Curved black claws/fingers
  box("finger1", [0.025, 0.06, 0.025], [-0.02, -0.04, 0.02], 0, hand, { material: rBlackMat, rotation: [0.3, 0, 0] });
  box("finger2", [0.025, 0.06, 0.025], [0.02, -0.04, 0.02], 0, hand, { material: rBlackMat, rotation: [0.3, 0, 0] });
  box("thumb", [0.025, 0.04, 0.025], [side * 0.03, -0.02, -0.02], 0, hand, { material: rBlackMat });

  return { armG, forearmG };
};

const armL = makeArm(-1);
const armR = makeArm(1);

// Posture: relaxed & cute
armL.armG.rotation.x = 0.15;
armL.armG.rotation.z = -0.1;
armR.armG.rotation.x = -0.25;
armR.armG.rotation.z = 0.25;

// --- NECK & HEAD ---
cyl("neckCollar", 0.14, 0.14, 0.1, [0, 0.98, 0], 0, robot, { material: rBlackMat, segments: 14 });

const rHead = new THREE.Group();
rHead.position.set(0, 1.06, 0);
robot.add(rHead);

// Crisp Low-Poly Robot Head (Matching the iconic low-poly art direction)
const headShell = box("rHeadBox", [0.70, 0.50, 0.48], [0, 0.25, 0], 0xe8edf2, rHead, { material: rWhiteMat });
headShell.name = "interactiveRobot";
headShell.castShadow = true;

// Low-poly beveled top brow accent
box("rHeadBrow", [0.66, 0.05, 0.12], [0, 0.50, 0.18], 0xd5dde7, rHead, { material: rWhiteMat });

// Dynamic Canvas for Robot OLED Visor (Crisp anti-aliased smiling arches and expressions)
const visorCanvas = document.createElement("canvas");
visorCanvas.width = 512;
visorCanvas.height = 512;
const vCtx = visorCanvas.getContext("2d");
const visorTexture = new THREE.CanvasTexture(visorCanvas);

let robotExpression = "happy";
let nextSquintTime = performance.now() + 2500;
let isSquinting = false;
let squintEndTime = 0;

function drawRobotFace() {
  // Deep OLED black background
  vCtx.fillStyle = "#070b14";
  vCtx.fillRect(0, 0, 512, 512);

  // Outer screen bezel
  vCtx.strokeStyle = "#162032";
  vCtx.lineWidth = 14;
  vCtx.beginPath();
  vCtx.roundRect(14, 16, 484, 480, 48);
  vCtx.stroke();

  // Glowing cybernetic rim
  vCtx.strokeStyle = "rgba(0, 242, 254, 0.30)";
  vCtx.lineWidth = 3.5;
  vCtx.beginPath();
  vCtx.roundRect(26, 28, 460, 456, 40);
  vCtx.stroke();

  // Glow settings for eyes and smile
  vCtx.shadowColor = "#00f2fe";
  vCtx.shadowBlur = 24;

  if (robotExpression === "happy") {
    // --- CUTE ROUND CAPSULE EYES (Clean & Sweet) ---
    vCtx.fillStyle = "#00f2fe";
    vCtx.beginPath();
    vCtx.roundRect(142, 172, 60, 78, 28);
    vCtx.fill();

    vCtx.beginPath();
    vCtx.roundRect(310, 172, 60, 78, 28);
    vCtx.fill();

    // Specular Catchlights (Kawaii shine)
    vCtx.fillStyle = "#ffffff";
    vCtx.shadowBlur = 4;
    vCtx.beginPath();
    vCtx.arc(174, 192, 9, 0, Math.PI * 2);
    vCtx.fill();
    vCtx.beginPath();
    vCtx.arc(158, 222, 4.5, 0, Math.PI * 2);
    vCtx.fill();

    vCtx.beginPath();
    vCtx.arc(342, 192, 9, 0, Math.PI * 2);
    vCtx.fill();
    vCtx.beginPath();
    vCtx.arc(326, 222, 4.5, 0, Math.PI * 2);
    vCtx.fill();

  } else if (robotExpression === "squint" || robotExpression === "joy") {
    // --- HAPPY SQUINTING ARCHES (^  ^) ---
    vCtx.strokeStyle = "#00f2fe";
    vCtx.lineWidth = 16;
    vCtx.lineCap = "round";

    // Left eye squint arch ^
    vCtx.beginPath();
    vCtx.arc(172, 228, 38, 1.15 * Math.PI, 1.85 * Math.PI, false);
    vCtx.stroke();

    // Right eye squint arch ^
    vCtx.beginPath();
    vCtx.arc(340, 228, 38, 1.15 * Math.PI, 1.85 * Math.PI, false);
    vCtx.stroke();

    // Pure white core highlights on squint arches
    vCtx.strokeStyle = "#ffffff";
    vCtx.lineWidth = 5.5;
    vCtx.beginPath();
    vCtx.arc(172, 228, 38, 1.15 * Math.PI, 1.85 * Math.PI, false);
    vCtx.stroke();

    vCtx.beginPath();
    vCtx.arc(340, 228, 38, 1.15 * Math.PI, 1.85 * Math.PI, false);
    vCtx.stroke();

  } else if (robotExpression === "wink") {
    // Left eye wink arch ^
    vCtx.strokeStyle = "#00f2fe";
    vCtx.lineWidth = 16;
    vCtx.lineCap = "round";
    vCtx.beginPath();
    vCtx.arc(172, 228, 38, 1.15 * Math.PI, 1.85 * Math.PI, false);
    vCtx.stroke();
    vCtx.strokeStyle = "#ffffff";
    vCtx.lineWidth = 5.5;
    vCtx.beginPath();
    vCtx.arc(172, 228, 38, 1.15 * Math.PI, 1.85 * Math.PI, false);
    vCtx.stroke();

    // Right eye open with sparkle
    vCtx.fillStyle = "#00f2fe";
    vCtx.beginPath();
    vCtx.roundRect(310, 172, 60, 78, 28);
    vCtx.fill();
    vCtx.fillStyle = "#ffffff";
    vCtx.beginPath();
    vCtx.arc(342, 192, 9, 0, Math.PI * 2);
    vCtx.fill();
    vCtx.beginPath();
    vCtx.arc(326, 222, 4.5, 0, Math.PI * 2);
    vCtx.fill();
  }

  // --- ROSY BLUSH CHEEKS (Soft & Cute) ---
  const isJoyful = robotExpression === "squint" || robotExpression === "joy";
  vCtx.fillStyle = isJoyful ? "rgba(255, 75, 155, 0.85)" : "rgba(255, 75, 155, 0.50)";
  vCtx.shadowColor = "rgba(255, 75, 155, 0.8)";
  vCtx.shadowBlur = 14;
  vCtx.beginPath();
  vCtx.ellipse(118, 268, 18, 9, 0, 0, Math.PI * 2);
  vCtx.fill();
  vCtx.beginPath();
  vCtx.ellipse(394, 268, 18, 9, 0, 0, Math.PI * 2);
  vCtx.fill();

  // --- GLOWING SMILE (Simple & Clean) ---
  vCtx.strokeStyle = "#00f2fe";
  vCtx.shadowColor = "#00f2fe";
  vCtx.shadowBlur = 20;
  vCtx.lineWidth = isJoyful ? 14 : 11;
  vCtx.lineCap = "round";
  vCtx.beginPath();
  vCtx.arc(256, 276, isJoyful ? 42 : 36, 0.18 * Math.PI, 0.82 * Math.PI, false);
  vCtx.stroke();

  // Inner white core highlight on smile
  vCtx.strokeStyle = "#ffffff";
  vCtx.lineWidth = 4;
  vCtx.beginPath();
  vCtx.arc(256, 276, isJoyful ? 42 : 36, 0.20 * Math.PI, 0.80 * Math.PI, false);
  vCtx.stroke();

  visorTexture.needsUpdate = true;
}
drawRobotFace();

// Recessed low-poly black glossy visor base
const visorMesh = box("rVisor", [0.58, 0.36, 0.05], [0, 0.25, 0.24], 0x06090f, rHead, {
  material: mat(0x06090f, 0.1, 0.9)
});
visorMesh.name = "interactiveRobot";

// OLED Flat Screen Plane with Canvas Texture
const screenGeo = new THREE.PlaneGeometry(0.56, 0.34);
const rScreenMat = new THREE.MeshBasicMaterial({
  map: visorTexture,
  transparent: false
});
const screenMesh = new THREE.Mesh(screenGeo, rScreenMat);
screenMesh.position.set(0, 0.25, 0.268);
screenMesh.name = "interactiveRobot";
rHead.add(screenMesh);

// Legacy references kept safe for compatibility
const rEyeMat = new THREE.MeshBasicMaterial({ visible: false });
const eyeL = new THREE.Group();
const eyeR = new THREE.Group();
const mouthMesh = new THREE.Group();

// Low-poly golden crest on top of head
const headCrest = cyl("headCrest", 0.08, 0.10, 0.05, [0, 0.525, 0], 0, rHead, {
  material: rGoldMat,
  segments: 8
});

// Low-Poly Head Side Communicators / Earmuffs (8 segments)
const makeHeadphone = (side) => {
  const earG = new THREE.Group();
  earG.position.set(side * 0.38, 0.25, 0);
  earG.rotation.y = side * (Math.PI / 2);
  rHead.add(earG);

  cyl("earSpacer", 0.11, 0.11, 0.05, [0, 0, 0], 0, earG, { material: rBlackMat, segments: 8 });
  cyl("earCup", 0.10, 0.10, 0.06, [0, 0, 0.04], 0, earG, { material: rGoldMat, segments: 8 });
  const earRim = new THREE.Mesh(new THREE.TorusGeometry(0.09, 0.018, 6, 12), rGoldMat);
  earRim.position.set(0, 0, 0.07);
  earG.add(earRim);
};
makeHeadphone(-1);
makeHeadphone(1);

// Low-Poly Black Antennae with Golden Ball Tips (Angled outward)
const makeAntenna = (side) => {
  const antG = new THREE.Group();
  antG.position.set(side * 0.24, 0.52, 0);
  antG.rotation.z = side * -0.25;
  rHead.add(antG);

  cyl("antStem", 0.018, 0.018, 0.24, [0, 0.12, 0], 0, antG, { material: rBlackMat, segments: 6 });
  const antOrb = new THREE.Mesh(new THREE.IcosahedronGeometry(0.048, 1), rGoldMat);
  antOrb.position.set(0, 0.25, 0);
  antG.add(antOrb);
  return antG;
};
const rAntennaL = makeAntenna(-1);
const rAntennaR = makeAntenna(1);

// Robot Joy Animation State
let robotJumpTimer = 0;
let isRobotJumping = false;

function triggerRobotJoy() {
  playClickSound("robot");
  isRobotJumping = true;
  robotJumpTimer = 0;
  robotExpression = "joy";
  drawRobotFace();
  setTimeout(() => {
    robotExpression = "wink";
    drawRobotFace();
    setTimeout(() => {
      robotExpression = "happy";
      drawRobotFace();
      nextSquintTime = performance.now() + 3000;
    }, 700);
  }, 900);
}

// --- INTERACTIVE SMARTPHONE ON ALUMINUM DESKTOP STAND ---
const phoneStandGroup = new THREE.Group();
phoneStandGroup.position.set(-1.68, 1.73, 0.68);
phoneStandGroup.rotation.y = 0.35; // Angled facing the user/camera
desk.add(phoneStandGroup);

// Sleek aluminum desktop stand
box("dockBase", [0.42, 0.024, 0.44], [0, 0.012, 0], 0x161e2b, phoneStandGroup, { metalness: 0.85, roughness: 0.25 });
box("dockPillar", [0.08, 0.34, 0.04], [0, 0.17, -0.06], 0x131924, phoneStandGroup, { rotation: [-0.35, 0, 0], metalness: 0.85 });
box("dockBackPlate", [0.38, 0.58, 0.02], [0, 0.32, -0.01], 0x182230, phoneStandGroup, { rotation: [-0.35, 0, 0], metalness: 0.85 });
box("dockShelf", [0.38, 0.04, 0.08], [0, 0.10, 0.10], 0x161e2b, phoneStandGroup, { metalness: 0.85 });

// Smartphone resting upright in the dock
const phoneGroup = new THREE.Group();
phoneGroup.position.set(0, 0.12, 0.06);
phoneGroup.rotation.x = -0.35; // Angled upright at 65 degrees
phoneStandGroup.add(phoneGroup);

// Phone body (Midnight black aluminum casing with camera bump)
const phoneBody = box("phoneBody", [0.44, 0.88, 0.026], [0, 0.38, 0], 0x0f1420, phoneGroup, { roughness: 0.25, metalness: 0.85 });
phoneBody.name = "interactivePhone";

// Camera bump on the back
box("phoneCamBump", [0.14, 0.14, 0.015], [-0.10, 0.68, -0.018], 0x161d28, phoneGroup, { metalness: 0.9 });

// Dynamic canvas for smartphone screen
const phoneCanvas = document.createElement("canvas");
phoneCanvas.width = 256;
phoneCanvas.height = 512;
const pCtx = phoneCanvas.getContext("2d");
const phoneTexture = new THREE.CanvasTexture(phoneCanvas);

let phoneNotificationIndex = 0;
const PHONE_NOTIFICATIONS = [
  { app: "WHATSAPP", title: "Cliente 🚀", msg: "Progetto 3D approvato! Quando andiamo live?" },
  { app: "GITHUB", title: "davidebalice ✨", msg: "Commit: 60 FPS Three.js Hero deployed!" },
  { app: "CALENDAR", title: "Meeting ☕", msg: "Nuova call consulenza Creative 3D Coder" }
];

function drawPhoneScreen() {
  pCtx.fillStyle = "#0a0f1d";
  pCtx.fillRect(0, 0, 256, 512);

  // Soft gradient wallpaper
  const grad = pCtx.createLinearGradient(0, 0, 256, 512);
  grad.addColorStop(0, "rgba(0, 242, 254, 0.25)");
  grad.addColorStop(0.5, "rgba(121, 40, 202, 0.25)");
  grad.addColorStop(1, "rgba(10, 15, 29, 0.8)");
  pCtx.fillStyle = grad;
  pCtx.fillRect(0, 0, 256, 512);

  // Status Bar
  pCtx.font = "bold 16px 'JetBrains Mono', monospace";
  pCtx.fillStyle = "#e2e8f0";
  pCtx.fillText("09:41", 20, 28);
  pCtx.fillText("5G 100%", 170, 28);

  // Camera punch hole dot
  pCtx.fillStyle = "#000000";
  pCtx.beginPath();
  pCtx.arc(128, 20, 6, 0, Math.PI * 2);
  pCtx.fill();

  // Glass card notification
  const notif = PHONE_NOTIFICATIONS[phoneNotificationIndex];
  pCtx.fillStyle = "rgba(21, 30, 48, 0.9)";
  pCtx.beginPath();
  pCtx.roundRect(14, 70, 228, 120, 14);
  pCtx.fill();
  pCtx.strokeStyle = "rgba(0, 242, 254, 0.4)";
  pCtx.lineWidth = 1.5;
  pCtx.stroke();

  // Notification badge & text
  pCtx.font = "bold 11px 'JetBrains Mono', monospace";
  pCtx.fillStyle = "#00f2fe";
  pCtx.fillText(notif.app + " • ORA", 28, 96);

  pCtx.font = "bold 15px 'Plus Jakarta Sans', sans-serif";
  pCtx.fillStyle = "#ffffff";
  pCtx.fillText(notif.title, 28, 122);

  pCtx.font = "12px 'Plus Jakarta Sans', sans-serif";
  pCtx.fillStyle = "#94a3b8";
  pCtx.fillText(notif.msg.substring(0, 26), 28, 146);
  if (notif.msg.length > 26) {
    pCtx.fillText(notif.msg.substring(26), 28, 166);
  }

  // Interactive hint
  pCtx.font = "11px 'JetBrains Mono', monospace";
  pCtx.fillStyle = "rgba(255, 255, 255, 0.55)";
  pCtx.fillText("👆 Tocca per aprire", 55, 480);

  phoneTexture.needsUpdate = true;
}
drawPhoneScreen();

const phoneScreenMat = new THREE.MeshBasicMaterial({ map: phoneTexture });
const phoneScreenMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.40, 0.84), phoneScreenMat);
phoneScreenMesh.position.set(0, 0.38, 0.014);
phoneScreenMesh.name = "interactivePhone";
phoneGroup.add(phoneScreenMesh);

function triggerPhoneNotification() {
  playClickSound("phone");
  phoneNotificationIndex = (phoneNotificationIndex + 1) % PHONE_NOTIFICATIONS.length;
  drawPhoneScreen();
  // Quick bounce
  phoneGroup.position.y = 0.15;
  setTimeout(() => (phoneGroup.position.y = 0.12), 150);
}

// --- STUDIO AUDIO MONITORS (Speakers - Interactive Identical Pair) ---
const woofers = [];
let rightSpeakerGroup = null;
let leftSpeakerGroup = null;

const makeSpeaker = (x, z, rotY, isLeft = false) => {
  const spk = new THREE.Group();
  spk.position.set(x, 1.72, z);
  spk.rotation.y = rotY;
  desk.add(spk);
  if (isLeft) {
    leftSpeakerGroup = spk;
  } else {
    rightSpeakerGroup = spk;
  }

  // Speaker body
  const body = box("spkBody" + (isLeft ? "L" : "R"), [0.72, 1.15, 0.78], [0, 0.6, 0], 0x141a24, spk, { roughness: 0.4, metalness: 0.3 });
  body.name = "interactiveSpeaker";
  // Isolation angled wedge pad
  box("spkPad" + (isLeft ? "L" : "R"), [0.74, 0.06, 0.8], [0, 0.03, 0], 0x090d14, spk, { roughness: 0.9 });
  // Woofer cone (large - pulses on click)
  const wf = cyl("woofer" + (isLeft ? "L" : "R"), 0.22, 0.16, 0.08, [0, 0.45, 0.4], 0xd4a373, spk, { rotation: [Math.PI / 2, 0, 0], segments: 16 });
  wf.name = "interactiveSpeaker";
  woofers.push(wf);
  // Tweeter (small)
  cyl("tweeter" + (isLeft ? "L" : "R"), 0.09, 0.06, 0.06, [0, 0.88, 0.4], 0x00f2fe, spk, { rotation: [Math.PI / 2, 0, 0], segments: 12 });
  // Front power LED
  box("spkLed" + (isLeft ? "L" : "R"), [0.03, 0.03, 0.02], [isLeft ? -0.24 : 0.24, 0.14, 0.4], 0x00f2fe, spk, {
    emissive: 0x00f2fe,
    emissiveIntensity: 1.8,
    castShadow: false
  });

  // Dedicated soft studio monitor light so both speakers are equally visible, crisp, and identical in beauty
  const spkLight = new THREE.PointLight(0xdff4ff, 1.8, 3.2, 1.8);
  spkLight.position.set(0, 0.6, 0.9);
  spk.add(spkLight);

  return spk;
};
// Left speaker: placed outward on the left to frame the desk and rise clearly behind the robot
makeSpeaker(-3.15, -0.25, 0.26, true);
// Right speaker: placed in the back-right corner of the desk
makeSpeaker(3.38, -0.65, -0.36, false);

let speakerThumpTimer = 0;
function triggerSpeakerThump() {
  playClickSound("bass");
  speakerThumpTimer = 1.0;
}

// --- MECHANICAL KEYBOARD & GAMING MOUSE ---
const kb = new THREE.Group();
kb.scale.set(0.80, 0.80, 0.80);
kb.position.set(0, 1.75, 0.65);
desk.add(kb);

// Keyboard case (Interactive)
const kbCase = box("kbCase", [3.2, 0.12, 1.15], [0, 0.04, 0], 0x171d28, kb, { rotation: [0.03, 0, 0], roughness: 0.4 });
kbCase.name = "interactiveKeyboard";
// Sculpted keycaps
const keyMatDark = mat(0x273142, 0.5, 0.1);
const keyMatCyan = mat(0x00f2fe, 0.4, 0.2);
const keyMatAccent = mat(0xff007f, 0.4, 0.2);

for (let row = 0; row < 5; row++) {
  for (let col = 0; col < 14; col++) {
    const isSpecial = (row === 1 && (col >= 1 && col <= 3)) || (row === 4 && col >= 4 && col <= 9); // WASD & Space
    const isEsc = row === 0 && col === 0;
    const kColor = isEsc ? keyMatAccent : isSpecial ? keyMatCyan : keyMatDark;
    const kw = (row === 4 && col === 6) ? 0.9 : 0.16;
    if (row === 4 && (col === 5 || col === 7)) continue; // spacebar spacing

    box("key", [kw, 0.045, 0.16], [-1.35 + col * 0.21, 0.12, -0.38 + row * 0.2], 0, kb, {
      material: kColor,
      castShadow: false
    });
  }
}

// Gaming Mouse
const mouse = new THREE.Group();
mouse.position.set(1.72, 1.74, 0.65);
desk.add(mouse);

const mouseMesh = new THREE.Mesh(new THREE.SphereGeometry(0.24, 12, 8), mat(0x192130, 0.35, 0.4));
mouseMesh.scale.set(0.9, 0.45, 1.45);
mouseMesh.position.set(0, 0.08, 0);
mouse.add(mouseMesh);
// Glowing RGB scroll wheel
box("mouseWheel", [0.04, 0.06, 0.1], [0, 0.18, -0.1], 0x00f2fe, mouse, {
  emissive: 0x00f2fe,
  emissiveIntensity: 1.5,
  castShadow: false
});

// --- COFFEE ESPRESSO TAZZINA (Smooth ceramic, delicate Italian size) ---
const mugGroup = new THREE.Group();
mugGroup.position.set(2.14, 1.73, 0.86);
mugGroup.scale.set(0.44, 0.44, 0.44);
desk.add(mugGroup);

// Smooth ceramic saucer (piattino)
const saucer = cyl("mugSaucer", 0.46, 0.38, 0.04, [0, 0.02, 0], 0xf1f5f9, mugGroup, {
  segments: 32,
  roughness: 0.2
});
saucer.name = "interactiveMug";

const saucerRim = new THREE.Mesh(
  new THREE.TorusGeometry(0.44, 0.016, 12, 32),
  mat(0xf1f5f9, 0.2, 0.1)
);
saucerRim.position.set(0, 0.038, 0);
saucerRim.rotation.x = Math.PI / 2;
saucerRim.name = "interactiveMug";
mugGroup.add(saucerRim);

// Smooth ceramic cup body (32 radial segments for smooth curves)
const mugBodyMesh = cyl("mugBody", 0.30, 0.20, 0.42, [0, 0.23, 0], 0xf8fafc, mugGroup, {
  segments: 32,
  roughness: 0.18
});
mugBodyMesh.name = "interactiveMug";

// Smooth rounded ceramic lip / rim
const mugRim = new THREE.Mesh(
  new THREE.TorusGeometry(0.295, 0.018, 16, 32),
  mat(0xf8fafc, 0.18, 0.1)
);
mugRim.position.set(0, 0.44, 0);
mugRim.rotation.x = Math.PI / 2;
mugRim.name = "interactiveMug";
mugGroup.add(mugRim);

// Dark espresso liquid inside
cyl("coffee", 0.27, 0.27, 0.02, [0, 0.38, 0], 0x24140a, mugGroup, { segments: 32, roughness: 0.15 });

// Golden hazelnut crema layer ring
cyl("crema", 0.265, 0.265, 0.005, [0, 0.385, 0], 0xb37d46, mugGroup, { segments: 32, roughness: 0.35 });

// Smooth curved ceramic handle
const mugHandle = new THREE.Mesh(
  new THREE.TorusGeometry(0.13, 0.032, 16, 32, Math.PI * 1.2),
  mat(0xf8fafc, 0.18, 0.1)
);
mugHandle.position.set(0.30, 0.23, 0);
mugHandle.rotation.set(0, 0, -Math.PI / 2.3);
mugHandle.name = "interactiveMug";
mugGroup.add(mugHandle);

// Rising delicate steam particles
const steamParticles = [];
const steamMat = new THREE.MeshBasicMaterial({
  color: 0xffffff,
  transparent: true,
  opacity: 0.32
});
for (let p = 0; p < 5; p++) {
  const puff = new THREE.Mesh(new THREE.DodecahedronGeometry(0.045 + p * 0.012, 0), steamMat);
  puff.position.set(
    (Math.random() - 0.5) * 0.06,
    0.5 + p * 0.14,
    (Math.random() - 0.5) * 0.06
  );
  puff.userData = {
    baseY: 0.48,
    speed: 0.008 + Math.random() * 0.006,
    drift: (Math.random() - 0.5) * 0.003
  };
  mugGroup.add(puff);
  steamParticles.push(puff);
}

// Plant removed as requested
const leaves = [];
let plantWobbleTimer = 0;
function triggerPlantWobble() {}

// --- 3D LOGO SCULPTURE ON THE SPEAKER (Facing User / Camera) ---
const logoSpeakerGroup = new THREE.Group();
// Mounted directly on top of the right studio monitor speaker
logoSpeakerGroup.position.set(0, 1.18, 0);
// Angled directly facing the user / camera in the "Profilo Laterale" view
logoSpeakerGroup.rotation.y = 1.06;
if (rightSpeakerGroup) {
  rightSpeakerGroup.add(logoSpeakerGroup);
} else {
  desk.add(logoSpeakerGroup);
}

// Dedicated soft spotlight illuminating the logo from above-front
const logoSpot = new THREE.PointLight(0xffeedd, 3.2, 3.8, 1.6);
logoSpot.position.set(0.18, 0.65, 0.52);
logoSpeakerGroup.add(logoSpot);

let logoMeshGroup = null;
const gltfLoader = new GLTFLoader();

// Exact official branding colors matching user 2D logo
// Curve.003: Left bracket < (Coral Red #dd574e)
// Curve: Letter D (Dark Charcoal #292929)
// Curve.004: Slash cut inside D (Clean Crisp White #ffffff)
// Curve.001: Letter B (Slate Gray #5b5b5b)
// Curve.002: Right bracket > (Coral Red #dd574e)
const logoColorMap = {
  "Curve.003": { color: 0xdd574e, emissive: 0xdd574e, emissiveIntensity: 0.6 }, // < Coral Red
  "Curve":     { color: 0x2e2e2e, emissive: 0x222222, emissiveIntensity: 0.3 }, // D Charcoal
  "Curve.004": { color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 0.85 }, // Slashes White
  "Curve.001": { color: 0x7c7c7c, emissive: 0x4a4a4a, emissiveIntensity: 0.4 }, // B Slate Gray
  "Curve.002": { color: 0xdd574e, emissive: 0xdd574e, emissiveIntensity: 0.6 }, // > Coral Red
};

gltfLoader.load(
  "/assets/3dmodels/office/logo.gltf",
  (gltf) => {
    const model = gltf.scene;

    // Stand upright: 2D curves were modeled flat on X-Z plane, rotate -90 deg on X
    model.rotation.x = -Math.PI / 2;
    model.updateMatrixWorld(true);

    // Center geometry around origin and sit flush on base
    const bbox = new THREE.Box3().setFromObject(model);
    const size = bbox.getSize(new THREE.Vector3());
    const center = bbox.getCenter(new THREE.Vector3());

    model.position.x = -center.x;
    model.position.y = -bbox.min.y;
    model.position.z = -center.z;

    // Scaled to rest beautifully on top of the studio monitor speaker (larger and prominent)
    const targetScale = 0.82 / (size.x || 1);

    logoMeshGroup = new THREE.Group();
    logoMeshGroup.add(model);
    logoMeshGroup.scale.setScalar(targetScale);
    logoMeshGroup.position.set(0, 0, 0);

    model.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = false;
        child.receiveShadow = false;
        const style = logoColorMap[child.name] || { color: 0xdd574e, emissive: 0x331111, emissiveIntensity: 0.3 };
        child.material = new THREE.MeshStandardMaterial({
          color: style.color,
          emissive: style.emissive,
          emissiveIntensity: style.emissiveIntensity,
          metalness: 0.75,
          roughness: 0.25
        });
        child.name = "interactiveLogo";
        interactiveMeshes.push(child);
      }
    });

    const logoMeshes = interactiveMeshes.filter((o) => o.name === "interactiveLogo");
    logoMeshGroup.updateMatrixWorld(true);
    optimizeScene(logoMeshGroup);
    const keptInteractive = interactiveMeshes.filter((o) => !logoMeshes.includes(o));
    logoMeshGroup.traverse((o) => { if (o.isMesh) keptInteractive.push(o); });
    interactiveMeshes.length = 0;
    interactiveMeshes.push(...keptInteractive);

    logoSpeakerGroup.add(logoMeshGroup);
  },
  undefined,
  (err) => {
    console.warn("Could not load /assets/3dmodels/office/logo.gltf", err);
  }
);

// --- ERGONOMIC DESIGNER CHAIR (Full real scale, clearly elevated on floor) ---
const chair = new THREE.Group();
chair.scale.set(0.95, 0.95, 0.95);
chair.position.set(0.2, 0.04, 2.5);
chair.rotation.y = 2.8;
scene.add(chair);

// 5-star castor base
const starCenter = cyl("starCenter", 0.18, 0.18, 0.12, [0, 0.14, 0], 0x151b24, chair, { segments: 10, metalness: 0.8 });
for (let c = 0; c < 5; c++) {
  const cAngle = (c / 5) * Math.PI * 2;
  const castorArm = box("cArm" + c, [0.12, 0.08, 0.75], [Math.sin(cAngle) * 0.45, 0.12, Math.cos(cAngle) * 0.45], 0x151b24, chair, {
    rotation: [0, cAngle, 0],
    metalness: 0.8
  });
  // Castor wheel clearly on floor
  cyl("wheel" + c, 0.08, 0.08, 0.06, [Math.sin(cAngle) * 0.85, 0.08, Math.cos(cAngle) * 0.85], 0x090d14, chair, {
    rotation: [Math.PI / 2, 0, 0],
    segments: 8
  });
}

// Gas lift cylinder
cyl("gasLift", 0.09, 0.09, 0.68, [0, 0.52, 0], 0x222a38, chair, { metalness: 0.9, roughness: 0.2 });

// Curved ergonomic seat
const chairMat = mat(0x1a212e, 0.7, 0.1);
const seat = box("seat", [1.8, 0.22, 1.7], [0, 0.9, 0], 0x1e2736, chair, { material: chairMat });

// Lumbar backrest connected solidly to the seat (No gap!)
const backrest = new THREE.Group();
backrest.position.set(0, 0.98, -0.65);
backrest.rotation.x = -0.10;
chair.add(backrest);

// Heavy duty spine support and under-seat anchor bracket
box("spineSeatBracket", [0.24, 0.12, 0.65], [0, -0.05, 0.28], 0x141b25, backrest, { metalness: 0.85 });
box("spineSupport", [0.24, 1.55, 0.14], [0, 0.68, -0.12], 0x141b25, backrest, { metalness: 0.85 });

// Back cushion (flush against the seat)
box("backMain", [1.6, 1.48, 0.18], [0, 0.74, 0], 0x1a212e, backrest, { material: chairMat });
// Ergonomic lumbar curve pad
box("lumbarPad", [1.4, 0.45, 0.12], [0, 0.35, 0.06], 0x161d28, backrest, { material: chairMat });
// Headrest
box("headrest", [1.0, 0.38, 0.16], [0, 1.62, 0.05], 0x151b24, backrest, { material: chairMat });

// Armrests
const makeArmrest = (side) => {
  const arm = new THREE.Group();
  arm.position.set(side * 0.95, 0.9, 0);
  chair.add(arm);
  cyl("armPillar", 0.05, 0.05, 0.45, [0, 0.22, 0], 0x141a24, arm, { metalness: 0.8 });
  box("armPad", [0.18, 0.06, 0.85], [0, 0.45, 0.1], 0x0f1520, arm, { roughness: 0.5 });
};
makeArmrest(-1);
makeArmrest(1);

// --- FLOATING ATMOSPHERIC LIGHT PARTICLES (Dust / Fireflies) ---
const particlesGroup = new THREE.Group();
scene.add(particlesGroup);

const particleCount = 45;
const particleGeometry = new THREE.DodecahedronGeometry(0.025, 0);
const particleMaterial = new THREE.MeshBasicMaterial({
  color: 0x00f2fe,
  transparent: true,
  opacity: 0.22,
  depthWrite: false
});

// Le particelle sono semplici "segnaposto" di trasformazione: si disegnano tutte insieme con una InstancedMesh
const particles = [];
const particlesInstanced = new THREE.InstancedMesh(particleGeometry, particleMaterial, particleCount);
particlesInstanced.frustumCulled = false;
particlesGroup.add(particlesInstanced);
for (let pt = 0; pt < particleCount; pt++) {
  const pMesh = new THREE.Object3D();
  pMesh.position.set(
    (Math.random() - 0.5) * 11,
    0.8 + Math.random() * 5.5,
    (Math.random() - 0.5) * 8
  );
  pMesh.userData = {
    speedY: 0.003 + Math.random() * 0.004,
    driftX: (Math.random() - 0.5) * 0.004,
    driftZ: (Math.random() - 0.5) * 0.004,
    pulseSpeed: 1.5 + Math.random() * 2.5
  };
  pMesh.updateMatrix();
  particlesInstanced.setMatrixAt(pt, pMesh.matrix);
  particles.push(pMesh);
}

// --- CINEMATIC LIGHTING SUITE ---
const hemiLight = new THREE.HemisphereLight(currentPreset.hemiSky, currentPreset.hemiGround, 0.85);
scene.add(hemiLight);

// Key Directional Light (Moonlight / Window light)
const keyLight = new THREE.DirectionalLight(currentPreset.keyLight, 1.8);
keyLight.position.set(6.5, 8.5, 6.0);
keyLight.castShadow = true;
keyLight.shadow.mapSize.set(1024, 1024);
keyLight.shadow.bias = -0.0004;
keyLight.shadow.camera.left = -9;
keyLight.shadow.camera.right = 9;
keyLight.shadow.camera.top = 9;
keyLight.shadow.camera.bottom = -9;
scene.add(keyLight);

// Subtle fill light from room left
const fillLight = new THREE.PointLight(0x5a7ebb, 3.2, 12, 1.8);
fillLight.position.set(-5.0, 3.5, 2.0);
scene.add(fillLight);

// --- RAYCASTER FOR 3D INTERACTION ---
const raycaster = new THREE.Raycaster();
const mouseCoords = new THREE.Vector2(0, 0);
let hasMovedMouse = false;
let hoveredObject = null;

const interactiveMeshes = [
  lampShade,
  bulb,
  lampSwitchMesh,
  rChassis,
  bodyMesh,
  headShell,
  visorMesh,
  screenSurface,
  screenbarHousing,
  underdeskStrip,
  kbCase,
  mugBodyMesh,
  phoneBody,
  phoneScreenMesh,
  acousticPanel,
  ...woofers
];
interactiveMeshes.push(...wallInteractive);

const onPointerMove = (e) => {
  hasMovedMouse = true;
  mouseCoords.x = (e.clientX / window.innerWidth) * 2 - 1;
  mouseCoords.y = -(e.clientY / window.innerHeight) * 2 + 1;
};

window.addEventListener("pointermove", onPointerMove);

const onWindowClick = () => {
  if (!hoveredObject) return;

  if (hoveredObject.name === "interactiveLamp") {
    playClickSound("lamp");
    setLampState(!lampIsOn);
  } else if (hoveredObject.name === "interactiveRobot") {
    triggerRobotJoy();
  } else if (hoveredObject.name === "interactiveScreen") {
    playClickSound("click");
    currentCodeSnippet = (currentCodeSnippet + 1) % SNIPPETS.length;
    drawTerminal();
  } else if (hoveredObject.name === "interactiveScreenbar") {
    playClickSound("click");
    cycleScreenbar();
  } else if (hoveredObject.name === "interactiveUnderdesk") {
    playClickSound("click");
    cycleUnderdeskRgb();
  } else if (hoveredObject.name === "interactiveKeyboard") {
    playClickSound("click");
    currentCodeSnippet = (currentCodeSnippet + 1) % SNIPPETS.length;
    drawTerminal();
  } else if (hoveredObject.name === "interactiveMug") {
    playClickSound("mug");
    steamParticles.forEach((p) => {
      p.userData.speed *= 2.5;
      setTimeout(() => {
        p.userData.speed /= 2.5;
      }, 800);
    });
  } else if (hoveredObject.name === "interactivePhone") {
    triggerPhoneNotification();
  } else if (hoveredObject.name === "interactiveSpeaker") {
    triggerSpeakerThump();
  } else if (hoveredObject.name === "interactiveWallLight") {
    toggleWallLight();
  } else if (hoveredObject.name === "interactiveProject") {
    playClickSound("click");
    navigateRef.current(hoveredObject.userData.link);
  } else if (hoveredObject.name === "interactiveNeon") {
    playClickSound("lamp");
    neonIsOn = !neonIsOn;
    drawNeon();
  } else if (hoveredObject.name === "interactivePlant") {
    triggerPlantWobble();
  } else if (hoveredObject.name === "interactiveLogo") {
    playClickSound("click");
    if (logoMeshGroup) {
      const baseRot = logoMeshGroup.rotation.y;
      let p = 0;
      const spin = () => {
        p += 0.08;
        logoMeshGroup.rotation.y = baseRot + p * Math.PI * 2;
        if (p < 1) requestAnimationFrame(spin);
        else logoMeshGroup.rotation.y = baseRot;
      };
      spin();
    }
  }
};
window.addEventListener("click", onWindowClick);



// Lamp toggle button in drawer
const lampSwitchBtn = document.querySelector("#lampSwitch");
if (lampSwitchBtn) {
  lampSwitchBtn.addEventListener("click", () => {
    playClickSound("lamp");
    setLampState(!lampIsOn);
  });
}



// Particles toggle in drawer
const particlesSwitchBtn = document.querySelector("#particlesSwitch");
if (particlesSwitchBtn) {
  particlesSwitchBtn.addEventListener("click", () => {
    particlesGroup.visible = !particlesGroup.visible;
    particlesSwitchBtn.classList.toggle("active", particlesGroup.visible);
    particlesSwitchBtn.textContent = particlesGroup.visible ? "ATTIVE" : "DISATTIVE";
    playClickSound("click");
  });
}

// Robot trigger button in drawer
const robotTriggerBtn = document.querySelector("#robotTrigger");
if (robotTriggerBtn) {
  robotTriggerBtn.addEventListener("click", () => {
    triggerRobotJoy();
  });
}

// Screen theme button in drawer
const screenCycleBtn = document.querySelector("#screenCycle");
if (screenCycleBtn) {
  screenCycleBtn.addEventListener("click", () => {
    playClickSound("click");
    currentCodeSnippet = (currentCodeSnippet + 1) % SNIPPETS.length;
    drawTerminal();
  });
}

// Phone trigger in drawer
const phoneTriggerBtn = document.querySelector("#phoneTrigger");
if (phoneTriggerBtn) {
  phoneTriggerBtn.addEventListener("click", () => {
    triggerPhoneNotification();
  });
}

// Speaker sub-bass trigger in drawer
const speakerTriggerBtn = document.querySelector("#speakerTrigger");
if (speakerTriggerBtn) {
  speakerTriggerBtn.addEventListener("click", () => {
    triggerSpeakerThump();
  });
}

// Studio wall light trigger in drawer
const wallLightTriggerBtn = document.querySelector("#wallLightTrigger");
if (wallLightTriggerBtn) {
  wallLightTriggerBtn.addEventListener("click", () => {
    toggleWallLight();
  });
}

// Plant wobble trigger in drawer
const plantTriggerBtn = document.querySelector("#plantTrigger");
if (plantTriggerBtn) {
  plantTriggerBtn.addEventListener("click", () => {
    triggerPlantWobble();
  });
}

// --- ANIMATION LOOP & FPS COUNTER ---
const clock = new THREE.Clock();
let frameCount = 0;
let lastFpsTime = performance.now();

let targetCamX = 2.6;
let targetCamY = 3.85;

let isVisibleOnScreen = true;
const onScroll = () => {
  isVisibleOnScreen = window.scrollY < (window.innerHeight * 1.15);
};
window.addEventListener("scroll", onScroll, { passive: true });

let animId = null;
function animate() {
  animId = requestAnimationFrame(animate);
  if (!isVisibleOnScreen) return;

  const t = clock.getElapsedTime();
  const now = performance.now();

  const normX = Math.max(-1.0, Math.min(1.0, mouseCoords.x));
  const normY = Math.max(-1.0, Math.min(1.0, mouseCoords.y));

  // Smooth mouse parallax camera around Profilo Laterale view
  const isMobNow = (mount.clientWidth || window.innerWidth) < 768;
  const baseCamX = isMobNow ? 9.0 : 8.4;
  const baseCamY = isMobNow ? 3.6 : 3.1;
  const baseCamZ = isMobNow ? 5.2 : 3.8;
  const targetCamX = baseCamX + (isMobNow ? normX * 0.12 : normX * 0.35);
  const targetCamY = baseCamY + (isMobNow ? normY * 0.08 : normY * 0.22);
  camera.position.x += (targetCamX - camera.position.x) * 0.045;
  camera.position.y += (targetCamY - camera.position.y) * 0.045;
  camera.position.z += (baseCamZ - camera.position.z) * 0.045;
  camera.lookAt(cameraTarget);

  // Animate Coffee Steam
  steamParticles.forEach((puff, idx) => {
    puff.position.y += puff.userData.speed;
    puff.position.x += puff.userData.drift + Math.sin(t * 2 + idx) * 0.001;
    puff.scale.setScalar(1 + (puff.position.y - puff.userData.baseY) * 1.5);
    puff.material.opacity = Math.max(0, 0.4 - (puff.position.y - puff.userData.baseY) * 0.7);

    if (puff.position.y > puff.userData.baseY + 0.65) {
      puff.position.y = puff.userData.baseY;
      puff.position.x = (Math.random() - 0.5) * 0.06;
      puff.position.z = (Math.random() - 0.5) * 0.06;
    }
  });

  // Animate Floating Particles
  particles.forEach((p, idx) => {
    p.position.y += Math.sin(t * p.userData.pulseSpeed + idx) * 0.003;
    p.position.x += p.userData.driftX;
    p.position.z += p.userData.driftZ;
    if (p.position.x > 6) p.position.x = -6;
    if (p.position.x < -6) p.position.x = 6;
    if (p.position.z > 5) p.position.z = -5;
    if (p.position.z < -5) p.position.z = 5;
    p.updateMatrix();
    particlesInstanced.setMatrixAt(idx, p.matrix);
  });
  particlesInstanced.instanceMatrix.needsUpdate = true;

  // Animate blinking red beacons on skyscrapers
  const beaconBlink = (Math.floor(t * 1.6) % 2 === 0);
  beaconLights.forEach((bcn) => {
    bcn.material.emissiveIntensity = beaconBlink ? 2.5 : 0.2;
  });

  // Animate drifting low-poly clouds strictly inside the window opening (never clipping room wall)
  windowClouds.forEach((c) => {
    c.position.x += c.userData.speed;
    if (c.position.x > 2.35) {
      c.position.x = -2.1;
    }
    const x = c.position.x;
    let alpha = 0.82;
    if (x < -1.5) {
      alpha = 0.82 * Math.max(0, Math.min(1, (x - (-2.1)) / 0.6));
    } else if (x > 1.8) {
      alpha = 0.82 * Math.max(0, Math.min(1, (2.35 - x) / 0.55));
    }
    if (c.userData.cloudMat) {
      c.userData.cloudMat.opacity = alpha;
    }
  });

  // Woofer Thump Animation (Interactive audio monitors)
  if (speakerThumpTimer > 0) {
    speakerThumpTimer -= 0.05;
    const punch = Math.sin(speakerThumpTimer * Math.PI) * 0.16;
    woofers.forEach((wf) => {
      wf.position.z = 0.4 + punch;
      wf.scale.set(1 + punch * 0.6, 1 + punch * 0.6, 1 + punch * 1.5);
    });
    if (speakerThumpTimer <= 0) {
      woofers.forEach((wf) => {
        wf.position.z = 0.4;
        wf.scale.set(1, 1, 1);
      });
    }
  }

  // Plant Leaves Spring Wobble Animation (Interactive monstera)
  if (plantWobbleTimer > 0) {
    plantWobbleTimer -= 0.032;
    const wobble = Math.sin(plantWobbleTimer * Math.PI * 6) * plantWobbleTimer * 0.28;
    leaves.forEach((lf, idx) => {
      lf.rotation.z = Math.sin(idx + t * 4) * wobble;
    });
  }

  // Animate Robot Head Tracking Mouse & Idle Bobbing
  rHead.rotation.y = THREE.MathUtils.lerp(rHead.rotation.y, normX * 0.55, 0.08);
  rHead.rotation.x = THREE.MathUtils.lerp(rHead.rotation.x, -normY * 0.35, 0.08);
  rAntennaL.rotation.z = 0.25 + Math.sin(t * 4) * 0.08;
  rAntennaR.rotation.z = -0.25 - Math.sin(t * 4) * 0.08;

  // Animate BOTH Arms gracefully (Cute natural balance and waving on both sides)
  if (isRobotJumping) {
    armR.armG.rotation.z = 0.85;
    armR.forearmG.rotation.x = -0.7;
    armL.armG.rotation.z = -0.85;
    armL.forearmG.rotation.x = -0.7;
  } else {
    // Right arm welcoming wave
    armR.forearmG.rotation.x = -0.35 + Math.sin(t * 3.2) * 0.22;
    armR.armG.rotation.z = 0.38 + Math.cos(t * 3.2) * 0.14;
    // Left arm natural balancing & gesturing
    armL.forearmG.rotation.x = -0.30 + Math.sin(t * 2.8 + 1.2) * 0.20;
    armL.armG.rotation.z = -0.32 - Math.cos(t * 2.8) * 0.14;
  }

  // Wheel Idle Rocking and Spin on Joy
  wheelL.rotation.x = isRobotJumping ? (robotJumpTimer * Math.PI * 8) : (Math.sin(t * 2.5) * 0.15);
  wheelR.rotation.x = isRobotJumping ? (robotJumpTimer * Math.PI * 8) : (Math.sin(t * 2.5) * 0.15);

  // Periodic cute squint eye animation ("strizza occhi")
  if (!isRobotJumping && robotExpression === "happy" && !isSquinting && now > nextSquintTime) {
    isSquinting = true;
    robotExpression = "squint";
    drawRobotFace();
    squintEndTime = now + 420; // Happy squint duration
  } else if (isSquinting && now > squintEndTime) {
    isSquinting = false;
    robotExpression = "happy";
    drawRobotFace();
    nextSquintTime = now + 3200 + Math.random() * 2600; // Next squint in 3.2 - 5.8s
  }

  // Robot Joy Jump Animation
  if (isRobotJumping) {
    robotJumpTimer += 0.045;
    robot.position.y = 1.74 + Math.sin(robotJumpTimer * Math.PI) * 0.45;
    robot.rotation.y = 0.38 + robotJumpTimer * Math.PI * 2;
    if (robotJumpTimer >= 1.0) {
      isRobotJumping = false;
      robot.position.y = 1.74;
      robot.rotation.y = 0.38;
    }
  } else {
    // Minimal idle hover fluttuazione (very subtle & stable)
    robot.position.y = 1.74 + Math.sin(t * 1.5) * 0.003;
    robot.rotation.y = 0.38 + Math.sin(t * 1.2) * 0.006;
  }

  // Subtle breathing idle motion for desktop 3D logo
  // Parete: mappamondo che gira piano, neon con leggero sfarfallio
  globe.rotation.y += 0.004;
  if (neonIsOn) {
    neonMat.opacity = Math.sin(t * 50) > 0.97 && Math.sin(t * 0.7) > 0.8 ? 0.55 : 1;
  }

  if (logoMeshGroup) {
    logoMeshGroup.position.y = 0.005 + Math.sin(t * 1.5) * 0.003;
  }

  // Raycasting for interactive objects (Throttled every 4th frame for maximum performance)
  if (hasMovedMouse && frameCount % 4 === 0) {
    raycaster.setFromCamera(mouseCoords, camera);
    const intersects = raycaster.intersectObjects(interactiveMeshes, true);
    if (intersects.length > 0) {
      const hit = intersects[0].object;
      hoveredObject = hit;
      document.body.style.cursor = "pointer";
    } else {
      hoveredObject = null;
      document.body.style.cursor = "default";
    }
  }

  // FPS tracking
  frameCount++;
  if (now - lastFpsTime >= 1000) {
    frameCount = 0;
    lastFpsTime = now;
  }

  renderer.render(scene, camera);
}

// Steady terminal cursor blink update (decoupled from render loop for 60 FPS)
const terminalInterval = setInterval(drawTerminal, 500);

// --- DRAW CALL OPTIMIZATION: unisce le mesh statiche (stesso aspetto, molte meno draw call) ---
optimizeScene(scene, {
  rigidBodies: [
    robot, rHead, armR.armG, armR.forearmG, armL.armG, armL.forearmG, wheelL, wheelR, rAntennaL, rAntennaR,
    phoneGroup, particlesGroup, logoSpeakerGroup, rightSpeakerGroup,
    acousticPanel, lampGroup, lowerArm, upperArm, lampHead, screenbar, kb, mouse,
    ...windowClouds, ...woofers, ...steamParticles,
    neonGroup, shelfA, shelfB, shelfC, globe, credenza, floorPlant,
  ],
  keep: [underdeskStrip, ...beaconLights, ...steamParticles, ...woofers, ...leaves, ...wallKeep],
  protectedMaterials: [
    ...spotLenses, ...spotCones, horizonHazeMat, bulbMat, particleMaterial, screenMat, skyBackdropMat,
  ],
});

// Le mesh cliccabili ora sono unite per nome: ricostruisco la lista del raycast
const interactiveNames = new Set(interactiveMeshes.filter((o) => o.isMesh).map((o) => o.name));
const interactiveGroups = interactiveMeshes.filter((o) => !o.isMesh);
const rebuiltInteractive = new Set([...interactiveGroups, ...woofers]);
scene.traverse((o) => {
  if (!o.isMesh || !interactiveNames.has(o.name)) return;
  for (let q = o.parent; q; q = q.parent) if (interactiveGroups.includes(q)) return;
  rebuiltInteractive.add(o);
});
interactiveMeshes.length = 0;
interactiveMeshes.push(...rebuiltInteractive);

animate();

// --- RESIZE HANDLER WITH RESPONSIVE FOV & FRAMING ---
const onResize = () => {
  if (!mount) return;
  const rw = mount.clientWidth || window.innerWidth;
  const rh = mount.clientHeight || (rw < 768 ? 440 : 520);
  camera.aspect = rw / rh;
  const isMobileNow = rw < 768;
  camera.fov = isMobileNow ? 42 : 33;
  camera.position.set(isMobileNow ? 9.0 : 8.4, isMobileNow ? 3.6 : 3.1, isMobileNow ? 5.2 : 3.8);
  cameraTarget.set(isMobileNow ? 0.6 : 0.8, isMobileNow ? 1.6 : 1.8, 0.1);
  camera.updateProjectionMatrix();
  camera.lookAt(cameraTarget);
  renderer.setSize(rw, rh);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isMobileNow ? 1.0 : 1.15));
};
window.addEventListener("resize", onResize);

return () => {
  if (animId) cancelAnimationFrame(animId);
  clearInterval(terminalInterval);
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("click", onWindowClick);
  window.removeEventListener("resize", onResize);
  window.removeEventListener("scroll", onScroll);
  document.body.style.cursor = "default";
  renderer.dispose();
  if (mount && renderer.domElement && mount.contains(renderer.domElement)) {
    mount.removeChild(renderer.domElement);
  }
};


  }, []);

  return (
    <main className="hero internal-hero">
      <div id="scene" ref={mountRef}></div>

      <section className="copy internal-copy">
        <h1><span className="gradient-text">{text}</span></h1>
        {text2 && <p className="role" style={{ maxWidth: "460px", marginTop: "12px", fontSize: "1.1rem" }}>{text2}</p>}
      </section>

      <TechnoDivider />
    </main>
  );
}
